import React, { useState, useEffect, useRef, Component, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/Card';
import { Button } from '../components/Button';
// MIGRATION FIREBASE: 100% Firebase - Plus besoin de Railway
import { ordersService, settingsService, productsService, ticketZService, paymentsService } from '../services/firebaseService';
import useNotificationSound from '../hooks/useNotificationSound';
import { PaymentModal } from '../components/PaymentModal';
import { CancellationModal } from '../components/CancellationModal';
import { ConfirmationModal } from '../components/ConfirmationModal';
import { useToast } from '../components/Toast';
import {
  DndContext,
  DragOverlay,
  useDraggable,
  useDroppable,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  Package, Clock, CheckCircle,
  Pause, Play, RefreshCw, LogOut, ChefHat,
  AlertTriangle, X, Timer, Ban, Truck,
  Phone, ArrowRight, Settings, Minus, Plus,
  CreditCard, XCircle, Printer, User, Loader,
  Archive, Eye, Calendar, FileText, Lock, GripVertical
} from 'lucide-react';

// Error Boundary pour capturer les crashes
class OrdersModeErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error('🔴 CRASH OrdersMode:', error);
    console.error('🔴 Stack:', errorInfo?.componentStack);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-red-50 flex items-center justify-center p-8">
          <div className="bg-white rounded-xl shadow-xl p-8 max-w-2xl w-full">
            <h1 className="text-2xl font-bold text-red-600 mb-4">🔴 ERREUR - Mode Commandes</h1>
            <p className="text-gray-700 mb-4">Une erreur s'est produite. Voici les détails :</p>
            <pre className="bg-gray-900 text-red-400 p-4 rounded-lg overflow-auto text-sm mb-4">
              {this.state.error?.toString()}
            </pre>
            <pre className="bg-gray-900 text-yellow-400 p-4 rounded-lg overflow-auto text-xs max-h-64">
              {this.state.errorInfo?.componentStack}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg font-bold hover:bg-blue-700"
            >
              🔄 Recharger la page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// FIX Build 220: Helper pour formater "il y a X minutes"
const formatTimeAgo = (date) => {
  if (!date) return '';
  const now = new Date();
  const diffMs = now - date;
  const diffSec = Math.floor(diffMs / 1000);
  const diffMin = Math.floor(diffSec / 60);
  const diffHour = Math.floor(diffMin / 60);

  if (diffSec < 10) return 'à l\'instant';
  if (diffSec < 60) return `il y a ${diffSec}s`;
  if (diffMin < 60) return `il y a ${diffMin}min`;
  if (diffHour < 24) return `il y a ${diffHour}h`;
  return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
};

// Helper pour extraire le message d'erreur (évite React error #31)
const getErrorMessage = (error) => {
  // Si c'est déjà une string
  if (typeof error === 'string') return error;

  // Si c'est un objet d'erreur API (FastAPI/Pydantic)
  const detail = error?.response?.data?.detail;
  if (detail) {
    // Si detail est une string
    if (typeof detail === 'string') return detail;
    // Si detail est un tableau d'erreurs
    if (Array.isArray(detail)) {
      return detail.map(d => d.msg || d.message || JSON.stringify(d)).join(', ');
    }
    // Si detail est un objet avec msg
    if (detail.msg) return detail.msg;
    if (detail.message) return detail.message;
    // Fallback: convertir en string
    return JSON.stringify(detail);
  }

  // Erreur standard JavaScript
  if (error?.message) return error.message;

  // Fallback
  return 'Erreur inconnue';
};

// Configuration des colonnes Kanban
const COLUMNS = [
  {
    id: 'new',
    status: 'new',
    label: '🆕 Nouvelles',
    icon: Package,
    bgColor: 'bg-red-500',
    lightBg: 'bg-red-50',
    borderColor: 'border-red-300',
    headerBg: 'bg-gradient-to-r from-red-500 to-red-600',
    nextStatus: 'in_preparation',
    nextAction: '🔥 Commencer'
  },
  {
    id: 'in_preparation',
    status: 'in_preparation',
    label: '👨‍🍳 En Préparation',
    icon: ChefHat,
    bgColor: 'bg-orange-500',
    lightBg: 'bg-orange-50',
    borderColor: 'border-orange-300',
    headerBg: 'bg-gradient-to-r from-orange-500 to-amber-500',
    nextStatus: 'ready',
    nextAction: '✅ Prête'
  },
  {
    id: 'ready',
    status: 'ready',
    label: '✅ Prêtes',
    icon: CheckCircle,
    bgColor: 'bg-green-500',
    lightBg: 'bg-green-50',
    borderColor: 'border-green-300',
    headerBg: 'bg-gradient-to-r from-green-500 to-emerald-600',
    nextStatus: 'completed',
    nextAction: '🎉 Terminé'
  },
  {
    id: 'completed',
    status: 'completed',
    label: '🎉 Terminées',
    icon: Archive,
    bgColor: 'bg-gray-500',
    lightBg: 'bg-gray-50',
    borderColor: 'border-gray-300',
    headerBg: 'bg-gradient-to-r from-gray-500 to-gray-600',
    nextStatus: null,
    nextAction: null
  }
  // Note: Colonne "Annulées" supprimée - affichage via modal séparé
];

export const OrdersMode = () => {
  const navigate = useNavigate();
  const { playNotificationSound } = useNotificationSound();
  const toast = useToast();

  // Build 232: Son d'alerte pour annulation client (différent de nouvelle commande)
  const playCancelAlertSound = useCallback(() => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // Mélodie d'alerte descendante (urgente mais pas agressive)
      const alertMelody = [
        { freq: 880, start: 0, duration: 0.15, gain: 0.5 },      // La5
        { freq: 698.46, start: 0.15, duration: 0.15, gain: 0.5 }, // Fa5
        { freq: 523.25, start: 0.30, duration: 0.20, gain: 0.5 }, // Do5
        { freq: 440, start: 0.50, duration: 0.30, gain: 0.4 },    // La4 (grave)
        // Répétition
        { freq: 880, start: 1.0, duration: 0.15, gain: 0.5 },
        { freq: 698.46, start: 1.15, duration: 0.15, gain: 0.5 },
        { freq: 523.25, start: 1.30, duration: 0.20, gain: 0.5 },
        { freq: 440, start: 1.50, duration: 0.30, gain: 0.4 },
      ];

      alertMelody.forEach(note => {
        const oscillator = audioContext.createOscillator();
        const gainNode = audioContext.createGain();
        oscillator.connect(gainNode);
        gainNode.connect(audioContext.destination);
        oscillator.frequency.value = note.freq;
        oscillator.type = 'sawtooth'; // Son plus "alertant"

        const startTime = audioContext.currentTime + note.start;
        gainNode.gain.setValueAtTime(0, startTime);
        gainNode.gain.linearRampToValueAtTime(note.gain, startTime + 0.02);
        gainNode.gain.linearRampToValueAtTime(note.gain * 0.3, startTime + note.duration);

        oscillator.start(startTime);
        oscillator.stop(startTime + note.duration);
      });

      setTimeout(() => audioContext.close(), 2500);
    } catch (error) {
      console.log('Could not play cancel alert:', error);
    }
  }, []);

  // 🔐 H-03 FIX Build 305: Vérification de session avec token signé
  useEffect(() => {
    const session = localStorage.getItem('orders_mode_session');
    const expiry = localStorage.getItem('orders_mode_expiry');

    if (!session || !expiry) {
      navigate('/orders-mode-login', { replace: true });
      return;
    }

    // Vérifier que le token HMAC serveur est valide
    try {
      const tokenData = JSON.parse(atob(session));
      // Vérifier les champs requis (mode, created, expiry, sig)
      if (!tokenData.mode || !tokenData.created || !tokenData.expiry || !tokenData.sig) {
        throw new Error('Token invalide — champs manquants');
      }
      // Vérifier que le mode correspond
      if (tokenData.mode !== 'orders') {
        throw new Error('Token invalide — mauvais mode');
      }
      // Vérifier l'expiry intégrée au token (pas juste le localStorage)
      if (tokenData.expiry <= Date.now()) {
        throw new Error('Token expiré');
      }
    } catch (e) {
      // Token invalide (ancien format ou falsifié)
      localStorage.removeItem('orders_mode_session');
      localStorage.removeItem('orders_mode_expiry');
      navigate('/orders-mode-login', { replace: true });
      return;
    }

    const expiryDate = new Date(expiry);
    if (expiryDate <= new Date()) {
      localStorage.removeItem('orders_mode_session');
      localStorage.removeItem('orders_mode_expiry');
      navigate('/orders-mode-login', { replace: true });
    }
  }, [navigate]);

  const previousOrdersCount = useRef(0);
  const previousNewOrderIds = useRef(new Set());
  const previousCancelledIds = useRef(new Set()); // Build 232: Track cancelled orders
  const wakeLockRef = useRef(null);
  const isUpdatingRef = useRef(false); // Ref pour éviter le "bounce back" des commandes
  const [orders, setOrders] = useState([]);
  const [cancelledOrders, setCancelledOrders] = useState([]);
  const [blinkingCancelledIds, setBlinkingCancelledIds] = useState(new Set()); // Build 232: IDs qui clignotent
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showStockModal, setShowStockModal] = useState(false);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [showCancelledModal, setShowCancelledModal] = useState(false);
  const [stockFilter, setStockFilter] = useState('all');
  const [searchProduct, setSearchProduct] = useState('');
  const [wakeLockActive, setWakeLockActive] = useState(false);
  const [preparationTime, setPreparationTime] = useState(15);
  const [updatingOrder, setUpdatingOrder] = useState(null);

  // États pour les modals
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showCancellationModal, setShowCancellationModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  // États pour le système Z (clôture de journée)
  const [yesterdayStatus, setYesterdayStatus] = useState(null);
  const [pendingClosures, setPendingClosures] = useState([]); // Journées non clôturées
  const [showZModal, setShowZModal] = useState(false);
  const [selectedPendingDay, setSelectedPendingDay] = useState(null); // Jour sélectionné pour clôture
  const [zLoading, setZLoading] = useState(false);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [historicOrders, setHistoricOrders] = useState([]);
  const [historicTicketZ, setHistoricTicketZ] = useState(null);
  const [loadingHistoric, setLoadingHistoric] = useState(false);
  const [forceClosePin, setForceClosePin] = useState('');
  const [showForceClose, setShowForceClose] = useState(false);
  const [todayStatus, setTodayStatus] = useState(null);

  // État pour le drag & drop
  const [activeId, setActiveId] = useState(null);
  const [draggedOrder, setDraggedOrder] = useState(null);

  // Build 261: État pour l'imprimante Bluetooth (JAMAIS de disabled sur le bouton)
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerName, setPrinterName] = useState('');

  // État pour les onglets mobile (UX sans scroll horizontal)
  const [mobileActiveTab, setMobileActiveTab] = useState('new');

  // FIX Build 220: États pour auto-refresh et détection changement de jour
  const [lastLoadDate, setLastLoadDate] = useState(new Date().toDateString());
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [, setTick] = useState(0); // Force re-render pour mettre à jour formatTimeAgo
  const lastLoadDateRef = useRef(new Date().toDateString()); // Ref pour éviter closure stale
  const consecutiveErrorsRef = useRef(0); // BUG-1 fix: backoff sur erreurs Firebase
  const lastErrorTimeRef = useRef(0); // BUG-1 fix: timestamp dernière erreur

  // FIX Build 220: Mettre à jour l'affichage du temps écoulé toutes les 10s
  useEffect(() => {
    const ticker = setInterval(() => setTick(t => t + 1), 10000);
    return () => clearInterval(ticker);
  }, []);

  // ============================================
  // FIX Build 200: DÉTECTION MOBILE CORRIGÉE
  // Appel initial + listener resize
  // ============================================
  const [windowWidth, setWindowWidth] = useState(1024); // Valeur par défaut desktop

  useEffect(() => {
    // FIX Build 200: Appeler immédiatement au montage
    const updateWidth = () => setWindowWidth(window.innerWidth);
    updateWidth(); // ← IMPORTANT: Mise à jour initiale

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const isMobile = windowWidth < 768;

  // FIX Build 200: Configuration capteurs drag & drop - distance réduite
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5, // FIX: Réduit de 10 à 5 pour meilleure réactivité
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150, // FIX: Réduit de 200 à 150ms
        tolerance: 5,
      },
    })
  );

  // Wake Lock - Empêche l'écran de s'éteindre
  const requestWakeLock = async () => {
    try {
      if ('wakeLock' in navigator) {
        wakeLockRef.current = await navigator.wakeLock.request('screen');
        setWakeLockActive(true);
        console.log('🔆 Wake Lock activé');

        wakeLockRef.current.addEventListener('release', () => {
          setWakeLockActive(false);
        });
      }
    } catch (err) {
      console.error('Erreur Wake Lock:', err);
    }
  };

  const releaseWakeLock = async () => {
    if (wakeLockRef.current) {
      try {
        await wakeLockRef.current.release();
        wakeLockRef.current = null;
        setWakeLockActive(false);
      } catch (err) {
        console.error('Erreur libération Wake Lock:', err);
      }
    }
  };

  // FIX Build 220: Réactiver Wake Lock ET rafraîchir les commandes quand la page redevient visible
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        console.log('👀 Page visible - vérification et refresh...');

        // Réactiver le Wake Lock si nécessaire
        if (!wakeLockRef.current) {
          await requestWakeLock();
        }

        // Détecter changement de jour
        const todayStr = new Date().toDateString();
        if (todayStr !== lastLoadDateRef.current) {
          console.log('🌅 Nouveau jour détecté au réveil, refresh complet...');
          lastLoadDateRef.current = todayStr;
          setLastLoadDate(todayStr);
        }

        // Toujours rafraîchir les commandes au réveil (tablette peut avoir dormi longtemps)
        if (!isUpdatingRef.current) {
          await loadData();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    const session = localStorage.getItem('orders_mode_session');
    const expiry = localStorage.getItem('orders_mode_expiry');

    if (!session || !expiry || new Date(expiry) < new Date()) {
      navigate('/orders-mode-login');
      return;
    }

    requestWakeLock();
    loadData();
    const interval = setInterval(loadData, 5000); // 5 secondes - synchronisation plus rapide

    return () => {
      clearInterval(interval);
      releaseWakeLock();
    };
  }, [navigate]);

  const loadData = async () => {
    // BUG-1 fix: backoff exponentiel sur erreurs consécutives Firebase
    if (consecutiveErrorsRef.current > 0) {
      const backoffMs = Math.min(5000 * Math.pow(2, consecutiveErrorsRef.current - 1), 60000);
      if (Date.now() - lastErrorTimeRef.current < backoffMs) {
        return; // Pas encore le moment de réessayer
      }
    }

    // Ne pas recharger si une mise à jour est en cours (évite le "bounce back")
    if (isUpdatingRef.current) {
      return;
    }

    // FIX Build 220: Détecter changement de jour pendant le polling
    const todayStr = new Date().toDateString();
    if (todayStr !== lastLoadDateRef.current) {
      console.log('🌅 Nouveau jour détecté pendant polling, refresh complet...');
      lastLoadDateRef.current = todayStr;
      setLastLoadDate(todayStr);
    }

    try {
      // FIREBASE DIRECT
      const [ordersRes, settingsRes, productsRes] = await Promise.all([
        ordersService.getActive(),
        settingsService.get(),
        productsService.getAll()
      ]);

      const allOrders = ordersRes?.orders || [];

      // FIX Build 260: Filtrer par pickup_date = aujourd'hui
      // BK-45: Utiliser timezone Europe/Paris explicitement (pas l'heure locale du navigateur)
      const today = new Date().toLocaleDateString('fr-CA', { timeZone: 'Europe/Paris' }); // "YYYY-MM-DD"
      const extractDate = (val) => {
        if (!val) return '';
        // Helper pour formater une Date en YYYY-MM-DD local
        const formatLocal = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
        // String YYYY-MM-DD déjà formatée
        if (typeof val === 'string' && val.match(/^\d{4}-\d{2}-\d{2}/)) return val.slice(0, 10);
        // String YYYYMMDD (format getParisDateTime().dateStr)
        if (typeof val === 'string' && val.match(/^\d{8}$/)) return `${val.slice(0, 4)}-${val.slice(4, 6)}-${val.slice(6, 8)}`;
        // String ISO avec T (convertir en local)
        if (typeof val === 'string' && val.includes('T')) return formatLocal(new Date(val));
        // String autre format
        if (typeof val === 'string') return val.slice(0, 10);
        // Firestore Timestamp avec méthode toDate()
        if (val.toDate) return formatLocal(val.toDate());
        // Firestore Timestamp sérialisé {seconds, nanoseconds}
        if (val.seconds) return formatLocal(new Date(val.seconds * 1000));
        // Date JavaScript
        if (val instanceof Date) return formatLocal(val);
        if (val.toISOString) return formatLocal(new Date(val.toISOString()));
        return '';
      };

      const todayOrders = allOrders.filter(o => {
        const dateToCheck = o.pickup_date || o.created_at || o.order_date;
        return extractDate(dateToCheck) === today;
      });

      // Séparer les commandes actives et annulées (du jour uniquement)
      const activeOrders = todayOrders.filter(o => o.status !== 'cancelled');
      const cancelled = todayOrders.filter(o => o.status === 'cancelled');

      // Détecter les nouvelles commandes "new"
      const currentNewOrderIds = new Set(
        activeOrders.filter(o => o.status === 'new').map(o => o.id)
      );

      // Jouer le son si nouvelle commande détectée
      for (const orderId of currentNewOrderIds) {
        if (!previousNewOrderIds.current.has(orderId)) {
          playNotificationSound();
          break; // Un seul son même si plusieurs nouvelles commandes
        }
      }

      previousNewOrderIds.current = currentNewOrderIds;
      previousOrdersCount.current = activeOrders.length;

      // Build 232: Détecter les annulations par le client
      const currentCancelledIds = new Set(cancelled.map(o => o.id));
      for (const order of cancelled) {
        // Nouvelle annulation par le client ?
        if (!previousCancelledIds.current.has(order.id) && order.cancelled_by === 'client') {
          console.log(`🔴 Commande #${order.order_number || order.id?.slice(0, 6)} annulée par le client`);
          playCancelAlertSound();
          toast.error('❌ Commande annulée', `La commande #${order.order_number || order.id?.slice(0, 6)} a été annulée par le client`);

          // Ajouter au state des IDs qui clignotent
          setBlinkingCancelledIds(prev => new Set([...prev, order.id]));

          // Retirer du clignotement après 15 secondes
          setTimeout(() => {
            setBlinkingCancelledIds(prev => {
              const next = new Set(prev);
              next.delete(order.id);
              return next;
            });
          }, 15000);

          break; // Un seul son
        }
      }
      previousCancelledIds.current = currentCancelledIds;

      // BUG-1 fix: reset backoff sur succès
      consecutiveErrorsRef.current = 0;

      setOrders(activeOrders);
      setCancelledOrders(cancelled);

      const s = settingsRes?.settings || {};
      setIsPaused(s.is_paused || false);
      setPreparationTime(s.preparation_time_minutes || 15);

      // Vérifier les ruptures 24H expirées
      const prods = productsRes?.products || [];
      const now = new Date();
      prods.forEach(p => {
        if (p.out_of_stock_until) {
          const until = new Date(p.out_of_stock_until);
          if (until <= now && p.is_out_of_stock) {
            // FIREBASE DIRECT
            productsService.toggleStock(p.id, false);
          }
        }
      });
      setProducts(prods);

      // FIX Build 220: Mettre à jour le timestamp de dernière mise à jour
      setLastUpdate(new Date());
    } catch (error) {
      // BUG-1 fix: backoff exponentiel — max 60s entre les retries
      consecutiveErrorsRef.current += 1;
      lastErrorTimeRef.current = Date.now();
      const nextRetryIn = Math.min(5000 * Math.pow(2, consecutiveErrorsRef.current - 1), 60000);
      console.error(`Error loadData (retry ${consecutiveErrorsRef.current}, next in ${nextRetryIn / 1000}s):`, error.message || error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction refresh avec feedback visuel
  const handleRefresh = async () => {
    setRefreshing(true);
    await loadData();
    await fetchPendingClosures();
    setRefreshing(false);
  };

  // ============ SYSTÈME Z - CLÔTURE DE JOURNÉE ============

  // Formater une date en YYYY-MM-DD (en heure LOCALE, pas UTC)
  const formatDateISO = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Obtenir la date d'hier
  const getYesterdayDate = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return formatDateISO(yesterday);
  };

  // Obtenir la date d'aujourd'hui en format YYYY-MM-DD (heure locale)
  const getTodayDate = () => {
    return formatDateISO(new Date());
  };

  // Vérifier si une date est strictement dans le passé (avant aujourd'hui)
  // Retourne TRUE si dateStr < aujourd'hui, FALSE si dateStr >= aujourd'hui
  const isHistoricDate = (dateStr) => {
    if (!dateStr) return false;
    const today = getTodayDate();
    return dateStr < today; // Comparaison lexicographique fonctionne avec YYYY-MM-DD
  };

  // ============ HELPER PAIEMENT ============
  // Vérifie si une commande est considérée comme payée
  // - payment_status === 'paid'
  // - OU total === 0 et cashback/fidélité utilisé (= intégralement couvert)
  //
  // FIX 2026-08-20 (bug "commande payée en fidélité" sans débit réel) :
  // on ne fait plus confiance au SEUL payment_method ('loyalty'/'cashback') pour
  // décider qu'une commande est payée — une commande à 45€ avec seulement 2€ de
  // fidélité appliquée n'est PAS payée, les 43€ restants doivent être encaissés.
  // Seul le total réellement dû (calculé côté serveur) fait foi : s'il tombe à
  // 0€ grâce à la fidélité, c'est fiable depuis que createOrder ne débite/écrit
  // la commande que si le solde fidélité couvre bien ce montant
  // (cf. functions/index.js, transaction createOrder).
  const isOrderPaid = (order) => {
    if (!order) return false;

    // Statut explicitement payé
    if (order.payment_status === 'paid') return true;

    // Total à 0 avec cashback/fidélité utilisée = intégralement couvert
    if ((order.total === 0 || order.total === '0' || order.total === 0.00) && order.loyalty_used > 0) {
      return true;
    }

    return false;
  };

  // Vérifier le statut de clôture de la veille - CLOUD FUNCTION
  const checkYesterdayStatus = async () => {
    try {
      const yesterday = getYesterdayDate();
      console.log('🔍 checkYesterdayStatus - Vérification pour:', yesterday);

      const data = await ticketZService.getDailyStatus(yesterday);
      console.log('🔍 Yesterday status data:', data);
      setYesterdayStatus(data);
    } catch (error) {
      console.error('❌ Erreur vérification clôture:', error);
    }
  };

  // Vérifier le statut d'aujourd'hui (pour clôture anticipée) - CLOUD FUNCTION
  const checkTodayStatus = async () => {
    try {
      const today = formatDateISO(new Date());
      const data = await ticketZService.getDailyStatus(today);
      setTodayStatus(data);
    } catch (error) {
      console.error('Erreur vérification statut aujourd\'hui:', error);
    }
  };

  // Récupérer les journées non clôturées (pour le bandeau d'alerte) - CLOUD FUNCTION
  const fetchPendingClosures = async () => {
    try {
      const result = await ticketZService.getPendingDays();
      // S'assurer que data est un tableau
      const data = Array.isArray(result?.pending_days) ? result.pending_days : [];
      // Filtrer pour exclure aujourd'hui (on ne veut que les jours passés)
      const today = formatDateISO(new Date());
      const pastDays = data.filter(d => d?.date < today);
      setPendingClosures(pastDays);
    } catch (error) {
      console.error('Erreur récupération journées non clôturées:', error);
      setPendingClosures([]); // Reset en cas d'erreur
    }
  };

  // Ouvrir la modal pour clôturer un jour spécifique - CLOUD FUNCTION
  const openPendingDayModal = async (pendingDay) => {
    setZLoading(true);
    try {
      const dayStatus = await ticketZService.getDailyStatus(pendingDay.date);
      setSelectedPendingDay(dayStatus);
      setShowZModal(true);
    } catch (error) {
      console.error('Erreur chargement détails journée:', error);
      toast.error('Erreur', 'Impossible de charger les détails');
    } finally {
      setZLoading(false);
    }
  };

  // Clôturer une journée - CLOUD FUNCTION
  const handleCloseDay = async (dateToClose, forceClose = false, pinCode = null) => {
    setZLoading(true);
    try {
      const result = await ticketZService.closeDay(dateToClose, {
        force_close: forceClose,
        pin_code: pinCode
      });

      const ticketZ = result.ticket_z;
      toast.success('Journée clôturée', `Ticket Z créé - ${ticketZ.total_sales?.toFixed(2)}€`);
      setShowZModal(false);
      setSelectedPendingDay(null);
      setForceClosePin('');
      setShowForceClose(false);
      checkYesterdayStatus();
      checkTodayStatus();
      fetchPendingClosures();
    } catch (error) {
      toast.error('Erreur clôture', error.message);
    } finally {
      setZLoading(false);
    }
  };

  // Charger les commandes d'une date spécifique - FIREBASE DIRECT + CLOUD FUNCTION
  const loadHistoricData = async (date) => {
    console.log('📅 loadHistoricData appelé avec date:', date);

    // Si on sélectionne la date d'aujourd'hui, revenir à la vue normale
    const today = getTodayDate();
    console.log('📅 Comparaison: date=', date, 'today=', today, 'equal=', date === today);

    if (date === today) {
      console.log('📅 Date sélectionnée = aujourd\'hui, retour à la vue normale');
      backToToday();
      setShowDatePicker(false);
      return;
    }

    setLoadingHistoric(true);
    setSelectedDate(date);
    setHistoricOrders([]);
    setHistoricTicketZ(null);

    try {
      // Charger les commandes du jour - FIREBASE DIRECT
      const ordersResult = await ordersService.getByDate(date);
      setHistoricOrders(ordersResult.orders || []);

      // Charger le ticket Z si existant - CLOUD FUNCTION
      const statusData = await ticketZService.getDailyStatus(date);
      if (statusData.ticket_z) {
        setHistoricTicketZ(statusData.ticket_z);
      }
    } catch (error) {
      console.error('Erreur chargement historique:', error);
      toast.error('Erreur', 'Impossible de charger les données');
    } finally {
      setLoadingHistoric(false);
      setShowDatePicker(false);
    }
  };

  // Revenir aux commandes du jour
  const backToToday = () => {
    setSelectedDate(null);
    setHistoricOrders([]);
    setHistoricTicketZ(null);
  };

  // Imprimer le Ticket Z
  const printTicketZ = async (ticketZ, date) => {
    if (!ticketZ) {
      toast.warning('Pas de Ticket Z');
      return;
    }

    const service = await getPrinterService();
    const isReallyConnected = service.checkConnection();

    if (!isReallyConnected) {
      setPrinterConnected(false);
      setPrinterName('');
      toast.warning('Imprimante non connectée', 'Cliquez sur 🖨️ en haut');
      return;
    }

    try {
      toast.info('Impression Ticket Z...');
      const success = await service.printTicketZ(ticketZ, date);

      if (success) {
        toast.success('✅ Ticket Z imprimé !');
      } else {
        toast.error('Échec impression');
        setPrinterConnected(false);
        setPrinterName('');
      }
    } catch (error) {
      console.error('🖨️ Erreur Ticket Z:', error);
      toast.error('Erreur', error.message);
      setPrinterConnected(false);
      setPrinterName('');
    }
  };

  // Vérifier le statut Z au chargement
  useEffect(() => {
    checkYesterdayStatus();
    checkTodayStatus();
    fetchPendingClosures();
  }, []);

  // ============ FIN SYSTÈME Z ============

  // Obtenir le prochain statut
  const getNextStatus = (currentStatus, orderType) => {
    const flow = {
      'new': 'in_preparation',
      'in_preparation': 'ready',
      'ready': orderType === 'delivery' ? 'delivering' : 'completed',
      'delivering': 'completed'
    };
    return flow[currentStatus] || null;
  };

  // Configuration des confirmations par statut
  const getConfirmationConfig = (newStatus) => {
    const configs = {
      'in_preparation': {
        title: 'Commencer la préparation ?',
        message: 'La commande passera en statut "En préparation".',
        type: 'warning',
        confirmText: 'Oui, commencer'
      },
      'ready': {
        title: 'Marquer comme prête ?',
        message: 'La commande sera marquée comme prête pour le client.',
        type: 'success',
        confirmText: 'Oui, prête'
      },
      'delivering': {
        title: 'Partir en livraison ?',
        message: 'La commande passera en statut "En livraison".',
        type: 'warning',
        confirmText: 'Oui, partir'
      },
      'completed': {
        title: 'Terminer la commande ?',
        message: 'La commande sera marquée comme terminée.',
        type: 'success',
        confirmText: 'Oui, terminer'
      }
    };
    return configs[newStatus] || { title: 'Confirmer ?', message: '', type: 'warning', confirmText: 'Confirmer' };
  };

  // Demander confirmation avant changement de statut
  const requestStatusChange = (order, newStatus) => {
    // Bloquer si on termine sans paiement (utiliser isOrderPaid pour cashback/fidélité)
    if (newStatus === 'completed' && !isOrderPaid(order)) {
      toast.warning('Paiement requis', 'Veuillez encaisser avant de terminer la commande');
      setSelectedOrder(order);
      setShowPaymentModal(true);
      return;
    }

    // Confirmation requise pour TOUTES les transitions de statut
    setSelectedOrder(order);
    setPendingStatusChange(newStatus);
    setShowConfirmModal(true);
  };

  // Confirmer le changement de statut
  const confirmStatusChange = async () => {
    if (!pendingStatusChange || !selectedOrder) return;

    setShowConfirmModal(false);
    setUpdatingOrder(selectedOrder.id);
    isUpdatingRef.current = true;

    try {
      // Mise à jour optimiste
      if (selectedDate) {
        setHistoricOrders(prev => prev.map(o =>
          o.id === selectedOrder.id ? { ...o, status: pendingStatusChange } : o
        ));
      } else {
        setOrders(prev => prev.map(o =>
          o.id === selectedOrder.id ? { ...o, status: pendingStatusChange } : o
        ));
      }

      const skipNotification = selectedDate && isHistoricDate(selectedDate);
      // FIX Build 201: Passer skipNotification pour éviter les notifs sur régularisations
      await ordersService.updateStatus(selectedOrder.id, pendingStatusChange, skipNotification);

      if (skipNotification) {
        toast.success('Régularisation', `Commande #${selectedOrder.order_number || selectedOrder.id?.slice(0, 6)} mise à jour`);
      } else {
        toast.success('Statut mis à jour', `Commande #${selectedOrder.order_number || selectedOrder.id?.slice(0, 6)}`);
      }
      checkYesterdayStatus();
      checkTodayStatus();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur', getErrorMessage(error));
      if (selectedDate) {
        loadHistoricData(selectedDate);
      } else {
        loadData();
      }
    } finally {
      setUpdatingOrder(null);
      setPendingStatusChange(null);
      setSelectedOrder(null);
      setTimeout(() => { isUpdatingRef.current = false; }, 5000);
    }
  };

  // Changement de statut rapide (sans spinner, mise à jour optimiste immédiate)
  const handleQuickStatusChange = async (order, newStatus) => {
    // Bloquer si on termine sans paiement (utiliser isOrderPaid pour cashback/fidélité)
    if (newStatus === 'completed' && !isOrderPaid(order)) {
      toast.warning('Paiement requis', 'Veuillez encaisser avant de terminer la commande');
      setSelectedOrder(order);
      setShowPaymentModal(true);
      return;
    }

    // Bloquer le polling pendant la mise à jour
    isUpdatingRef.current = true;

    // Mise à jour optimiste IMMÉDIATE (pas de spinner)
    if (selectedDate) {
      setHistoricOrders(prev => prev.map(o =>
        o.id === order.id ? { ...o, status: newStatus } : o
      ));
    } else {
      setOrders(prev => prev.map(o =>
        o.id === order.id ? { ...o, status: newStatus } : o
      ));
    }

    // Appel API en arrière-plan (sans bloquer l'UI)
    try {
      // Si date historique, ne pas envoyer de notification (régularisation)
      // FIREBASE DIRECT (notifications push à gérer via Cloud Functions si nécessaire)
      await ordersService.updateStatus(order.id, newStatus);
      // Rafraîchir le bandeau "commandes en attente"
      checkYesterdayStatus();
      checkTodayStatus();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur', 'Échec de la mise à jour');
      // Recharger en cas d'erreur pour resync
      if (selectedDate) {
        loadHistoricData(selectedDate);
      } else {
        loadData();
      }
      // Délai court en cas d'erreur (1s) pour ne pas bloquer
      setTimeout(() => { isUpdatingRef.current = false; }, 1000);
      return;
    }
    // Délai plus long pour les succès (5s) pour éviter le bounce back
    setTimeout(() => { isUpdatingRef.current = false; }, 5000);
  };

  // Annuler une commande
  // FIX Build 201: Lancer une erreur explicite si pas de commande sélectionnée
  const handleCancelOrder = async (reason) => {
    if (!selectedOrder) {
      throw new Error('Aucune commande sélectionnée');
    }

    isUpdatingRef.current = true; // Bloquer le polling pendant la mise à jour

    try {
      // Mise à jour optimiste selon le contexte
      if (selectedDate) {
        setHistoricOrders(prev => prev.filter(o => o.id !== selectedOrder.id));
      } else {
        setOrders(prev => prev.filter(o => o.id !== selectedOrder.id));
      }

      // Si date historique, ne pas envoyer de notification (régularisation)
      // FIREBASE DIRECT (notifications push à gérer via Cloud Functions si nécessaire)
      const skipNotification = !!selectedDate; // Date historique = pas de notif
      await ordersService.cancel(selectedOrder.id, reason);
      if (skipNotification) {
        toast.success('Régularisation', `Commande #${selectedOrder.order_number || selectedOrder.id?.slice(0, 6)} annulée (sans notif)`);
      } else {
        toast.success('Commande annulée', `Commande #${selectedOrder.order_number || selectedOrder.id?.slice(0, 6)}`);
      }
      setShowCancellationModal(false);
      setSelectedOrder(null);

      // Recharger selon le contexte pour mettre à jour les annulées
      if (selectedDate) {
        loadHistoricData(selectedDate);
      } else {
        loadData();
      }
    } catch (error) {
      // FIX Build 200: Afficher l'erreur réelle
      console.error('Error cancelling order:', error);
      console.error('Error code:', error?.code);
      console.error('Error message:', error?.message);
      const errorMsg = error?.message || error?.code || 'Erreur inconnue';
      toast.error('Erreur annulation', errorMsg);
      // Recharger selon le contexte
      if (selectedDate) {
        loadHistoricData(selectedDate);
      } else {
        loadData();
      }
    } finally {
      // Délai avant de réactiver le polling
      setTimeout(() => { isUpdatingRef.current = false; }, 5000);
    }
  };

  // ===========================================
  // BUILD 261 - IMPRIMANTE BLUETOOTH SIMPLIFIÉE
  // Le service est un singleton global (window.__thermalPrintService)
  // Le bouton n'est JAMAIS désactivé
  // ===========================================

  // Fonction helper pour obtenir le service (toujours le même singleton)
  const getPrinterService = async () => {
    const service = (await import('../services/thermalPrintService')).default;
    return service;
  };

  // Sync l'état React avec le service au chargement
  useEffect(() => {
    const syncPrinterState = async () => {
      const service = await getPrinterService();

      // Callback pour détecter les déconnexions automatiques
      service.setOnDisconnect(() => {
        console.log('🖨️ Déconnexion détectée');
        setPrinterConnected(false);
        setPrinterName('');
        toast.warning('Imprimante déconnectée');
      });

      // Sync initial
      if (service.isConnected && service.device) {
        setPrinterConnected(true);
        setPrinterName(service.device.name || 'Imprimante');
      }
    };
    syncPrinterState();
  }, []);

  // Connecter/Déconnecter l'imprimante (bouton JAMAIS désactivé)
  const handleConnectPrinter = async () => {
    try {
      const service = await getPrinterService();

      console.log('🖨️ handleConnectPrinter, service ID:', service._id);
      const isCurrentlyConnected = service.checkConnection();
      console.log('🖨️ État actuel:', isCurrentlyConnected);

      if (isCurrentlyConnected) {
        // DÉCONNECTER
        service.disconnect();
        setPrinterConnected(false);
        setPrinterName('');
        toast.success('Imprimante déconnectée');
      } else {
        // CONNECTER
        toast.info('Recherche...', 'Sélectionnez votre imprimante');
        await service.connect();

        // Vérifier que la connexion est vraiment établie
        const nowConnected = service.checkConnection();
        console.log('🖨️ Après connect(), checkConnection:', nowConnected);

        if (nowConnected) {
          setPrinterConnected(true);
          setPrinterName(service.device?.name || 'Imprimante');
          toast.success('✅ Connectée !', service.device?.name || '');
        } else {
          throw new Error('Connexion établie mais vérification échouée');
        }
      }
    } catch (error) {
      console.error('🖨️ Erreur:', error);
      setPrinterConnected(false);
      setPrinterName('');

      const msg = error.message || '';
      if (msg.includes('annul') || msg.includes('cancel') || msg.includes('User cancelled')) {
        toast.warning('Connexion annulée');
      } else if (msg.includes('Bluetooth') || msg.includes('bluetooth')) {
        toast.error('Bluetooth requis', 'Utilisez Chrome ou Edge');
      } else if (msg.includes('compatible')) {
        toast.error('Imprimante non compatible', msg);
      } else {
        toast.error('Erreur connexion', msg || 'Réessayez');
      }
    }
  };

  // Imprimer une commande (utilise l'imprimante connectée)
  const printOrder = async (order) => {
    const service = await getPrinterService();

    console.log('🖨️ printOrder appelé, service ID:', service._id);

    // Vérifier connexion avec la méthode robuste
    const isReallyConnected = service.checkConnection();

    if (!isReallyConnected) {
      console.log('🖨️ Non connecté, sync état UI');
      setPrinterConnected(false);
      setPrinterName('');
      toast.warning('Imprimante non connectée', 'Cliquez sur 🖨️ en haut');
      return;
    }

    // Imprimer
    try {
      toast.info('Impression...', order.order_number || '');
      const success = await service.printOrderTicket(order);

      if (success) {
        toast.success('✅ Imprimé !', order.order_number || '');
      } else {
        toast.error('Échec impression', 'Vérifiez l\'imprimante');
        setPrinterConnected(false);
        setPrinterName('');
      }
    } catch (error) {
      console.error('🖨️ Erreur impression:', error);
      toast.error('Erreur', error.message);
      setPrinterConnected(false);
      setPrinterName('');
    }
  };

  // Imprimer un ticket cuisine (gros caracteres)
  const printKitchenOrder = async (order) => {
    const service = await getPrinterService();
    const isReallyConnected = service.checkConnection();

    if (!isReallyConnected) {
      setPrinterConnected(false);
      setPrinterName('');
      toast.warning('Imprimante non connectée', 'Cliquez sur 🖨️ en haut');
      return;
    }

    try {
      toast.info('Impression cuisine...', order.order_number || '');
      const success = await service.printKitchenTicket(order);

      if (success) {
        toast.success('Ticket cuisine imprimé !', order.order_number || '');
      } else {
        toast.error('Échec impression cuisine', 'Vérifiez l\'imprimante');
        setPrinterConnected(false);
        setPrinterName('');
      }
    } catch (error) {
      console.error('🖨️ Erreur impression cuisine:', error);
      toast.error('Erreur', error.message);
      setPrinterConnected(false);
      setPrinterName('');
    }
  };

  // Test impression diagnostic
  const handleTestPrint = async () => {
    const service = await getPrinterService();
    const isReallyConnected = service.checkConnection();

    if (!isReallyConnected) {
      setPrinterConnected(false);
      setPrinterName('');
      toast.warning('Imprimante non connectée');
      return;
    }

    try {
      toast.info('Test impression...');
      const success = await service.testPrint();

      if (success) {
        toast.success('Test OK !');
      } else {
        toast.error('Échec test impression');
      }
    } catch (error) {
      console.error('🖨️ Erreur test:', error);
      toast.error('Erreur test', error.message);
    }
  };

  const togglePause = async () => {
    // Confirmation avant de changer l'état
    const action = isPaused ? 'reprendre' : 'mettre en pause';
    const message = isPaused
      ? 'Les clients pourront à nouveau passer des commandes.'
      : '⚠️ Les clients ne pourront plus passer de commandes tant que la pause est active.';

    if (!window.confirm(`Voulez-vous ${action} les commandes ?\n\n${message}`)) {
      return;
    }

    try {
      // FIREBASE DIRECT
      await settingsService.update({ is_paused: !isPaused });
      setIsPaused(!isPaused);
      toast.success(
        isPaused ? 'Commandes reprises' : 'Commandes en pause',
        isPaused ? 'Les clients peuvent commander' : 'Les clients ne peuvent plus commander'
      );
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur', 'Impossible de changer l\'état');
    }
  };

  const updatePreparationTime = async (newTime) => {
    try {
      setPreparationTime(newTime);
      // FIREBASE DIRECT
      await settingsService.update({ preparation_time_minutes: newTime });
      toast.success('Temps mis à jour', `${newTime} minutes de préparation`);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur', 'Impossible de mettre à jour le temps');
    }
  };

  const setProductOutOfStock = async (productId, type) => {
    try {
      // FIREBASE DIRECT
      if (type === '24h') {
        const midnight = new Date();
        midnight.setHours(23, 59, 59, 999);
        await productsService.update(productId, {
          is_out_of_stock: true,
          out_of_stock_until: midnight.toISOString()
        });
        toast.warning('Rupture 24H', 'Produit en rupture jusqu\'à minuit');
      } else if (type === 'indefinite') {
        await productsService.update(productId, {
          is_out_of_stock: true,
          out_of_stock_until: null
        });
        toast.warning('Rupture indéfinie', 'Produit en rupture de stock');
      } else {
        await productsService.update(productId, {
          is_out_of_stock: false,
          out_of_stock_until: null
        });
        toast.success('Remis en stock', 'Produit disponible');
      }
      loadData();
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur', 'Impossible de mettre à jour le stock');
    }
  };

  const handleLogout = () => {
    releaseWakeLock();
    localStorage.removeItem('orders_mode_session');
    localStorage.removeItem('orders_mode_expiry');
    // Also clear admin session to require PIN for Back Office access
    localStorage.removeItem('admin_session');
    localStorage.removeItem('admin_session_expiry');
    navigate('/select-mode');
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStockStatus = (product) => {
    if (!product.is_out_of_stock) return 'in_stock';
    if (product.out_of_stock_until) return '24h';
    return 'indefinite';
  };

  const getOrdersForColumn = (status) => {
    // La colonne "completed" inclut aussi les commandes en livraison et livrées
    // BK-49: Limiter à 20 pour éviter ralentissement avec beaucoup de commandes
    if (status === 'completed') {
      return orders.filter(o => ['completed', 'delivered', 'delivering', 'done'].includes(o.status)).slice(0, 20);
    }
    // La colonne "cancelled" utilise le state séparé
    if (status === 'cancelled') {
      return cancelledOrders;
    }
    return orders.filter(o => o.status === status);
  };

  // ============ DRAG & DROP HANDLERS ============

  // Mapping des transitions de statut valides (permet de sauter des étapes)
  const VALID_STATUS_TRANSITIONS = {
    'new': ['in_preparation', 'ready', 'completed'],           // Nouvelle → tout statut suivant
    'in_preparation': ['ready', 'completed', 'delivering'],    // En prép → prête, terminée, ou livraison
    'ready': ['completed', 'delivering'],                       // Prête → terminée ou livraison
    'delivering': ['completed'],                                // En livraison → terminée
    'completed': []                                             // Terminée = fin
  };

  // Vérifier si une transition de statut est valide
  const isValidTransition = (fromStatus, toStatus) => {
    const validNextStatuses = VALID_STATUS_TRANSITIONS[fromStatus] || [];
    return validNextStatuses.includes(toStatus);
  };

  // Début du drag - bloquer le polling immédiatement
  const handleDragStart = (event) => {
    const { active } = event;
    const order = orders.find(o => o.id === active.id);
    if (order) {
      isUpdatingRef.current = true; // 🔒 Bloquer le polling dès le début du drag
      setActiveId(active.id);
      setDraggedOrder(order);
    }
  };

  // Fin du drag - appliquer le changement de statut
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    setActiveId(null);
    setDraggedOrder(null);

    // Si pas de destination valide, réactiver le polling et sortir
    if (!over) {
      setTimeout(() => { isUpdatingRef.current = false; }, 500);
      return;
    }

    const orderId = active.id;
    const newStatus = over.id; // L'id de la zone de drop est le statut de la colonne
    const order = orders.find(o => o.id === orderId);
    const oldStatus = order?.status;

    // Si même colonne ou pas de changement, réactiver le polling et sortir
    if (!order || order.status === newStatus) {
      setTimeout(() => { isUpdatingRef.current = false; }, 500);
      return;
    }

    // Vérifier si la transition est valide
    if (!isValidTransition(order.status, newStatus)) {
      toast.warning('Transition invalide', 'Cette commande ne peut pas être déplacée ici');
      setTimeout(() => { isUpdatingRef.current = false; }, 500);
      return;
    }

    // Bloquer si on termine sans paiement (utiliser isOrderPaid pour cohérence)
    if (newStatus === 'completed' && !isOrderPaid(order)) {
      toast.warning('Paiement requis', 'Veuillez encaisser avant de terminer');
      setSelectedOrder(order);
      setShowPaymentModal(true);
      setTimeout(() => { isUpdatingRef.current = false; }, 500);
      return;
    }

    // isUpdatingRef.current est déjà true depuis handleDragStart
    setUpdatingOrder(order.id);

    // 1️⃣ OPTIMISTIC UPDATE - Mettre à jour le state LOCAL immédiatement
    setOrders(prev => prev.map(o =>
      o.id === order.id ? { ...o, status: newStatus } : o
    ));

    // 2️⃣ FIREBASE DIRECT en arrière-plan
    try {
      await ordersService.updateStatus(order.id, newStatus);
      toast.success('Statut mis à jour', `Commande #${order.order_number || order.id?.slice(0, 6)}`);
      // PAS de loadData() ici - on a déjà mis à jour le state
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur', getErrorMessage(error));
      // 3️⃣ ROLLBACK si erreur - Remettre l'ancien statut
      setOrders(prev => prev.map(o =>
        o.id === order.id ? { ...o, status: oldStatus } : o
      ));
    } finally {
      setUpdatingOrder(null);
      // Attendre 6s (plus long que le polling de 5s) avant de réactiver
      setTimeout(() => { isUpdatingRef.current = false; }, 6000);
    }
  };

  // Pendant le drag - pour le feedback visuel
  const handleDragOver = (event) => {
    // Optionnel: ajouter un feedback visuel pendant le survol
  };

  // ============ FIN DRAG & DROP ============

  const outOfStockProducts = products.filter(p => p.is_out_of_stock === true);

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name?.toLowerCase().includes(searchProduct.toLowerCase());
    const matchesFilter = stockFilter === 'all' ||
      (stockFilter === 'out_of_stock' && p.is_out_of_stock);
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 overflow-x-hidden">
      {/* Header - Responsive mobile/tablette/desktop */}
      <div className="bg-gray-800 shadow-lg px-3 md:px-6 py-3 md:py-4 sticky top-0 z-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          {/* Titre et infos */}
          <div className="flex items-center gap-3 md:gap-4">
            <div className="w-10 h-10 md:w-14 md:h-14 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center flex-shrink-0">
              <ChefHat className="w-6 h-6 md:w-8 md:h-8 text-white" />
            </div>
            <div className="min-w-0">
              <h1 className="text-lg md:text-2xl font-bold text-white truncate">Mode Commandes</h1>
              <p className="text-orange-400 font-semibold text-xs md:text-base hidden md:block">
                📅 {(() => {
                  const today = new Date();
                  const options = { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' };
                  const dateStr = today.toLocaleDateString('fr-FR', options);
                  return dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
                })()}
              </p>
              <div className="flex items-center gap-2">
                <span className="text-blue-400 text-sm md:text-lg font-semibold">
                  {orders.length} active{orders.length > 1 ? 's' : ''}
                </span>
                {wakeLockActive && (
                  <span className="text-green-400 text-sm">🔆</span>
                )}
              </div>
            </div>
          </div>

          {/* Actions - Scrollable sur mobile */}
          <div className="flex items-center gap-2 overflow-x-auto pb-1 md:pb-0 -mx-3 px-3 md:mx-0 md:px-0 md:flex-wrap md:justify-end">
            {/* Bouton Historique / Sélecteur de date */}
            {/* Bouton Imprimante Bluetooth - JAMAIS désactivé */}
            <Button
              variant="outline"
              onClick={handleConnectPrinter}
              className={`px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border-2 min-h-[44px] flex-shrink-0 transition-all ${
                printerConnected
                  ? 'border-green-500 bg-green-500/20 text-green-400 hover:bg-green-500/30'
                  : 'border-gray-600 text-gray-300 hover:bg-gray-700'
              }`}
              title={printerConnected ? `✅ ${printerName} - Cliquer pour déconnecter` : '🖨️ Connecter une imprimante'}
            >
              <Printer className="w-5 h-5 md:w-6 md:h-6" />
              {printerConnected && (
                <span className="ml-1 md:ml-2 text-xs md:text-sm font-bold hidden md:inline">
                  {printerName.slice(0, 12)}
                </span>
              )}
            </Button>
            {/* Bouton Test Impression — visible quand connecte */}
            {printerConnected && (
              <Button
                variant="outline"
                onClick={handleTestPrint}
                className="px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border-2 border-blue-500 bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 min-h-[44px] flex-shrink-0"
                title="Test impression"
              >
                <span className="text-xs font-bold">TEST</span>
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowDatePicker(true)}
              className="px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border-2 border-gray-600 text-gray-300 hover:bg-gray-700 min-h-[44px] flex-shrink-0"
              title="Charger une journée"
            >
              <Calendar className="w-5 h-5 md:w-6 md:h-6" />
            </Button>
            {/* Bouton Ticket Z */}
            <Button
              variant="outline"
              onClick={() => setShowZModal(true)}
              className="px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border-2 border-gray-600 text-green-400 hover:bg-gray-700 min-h-[44px] flex-shrink-0"
              title="Clôture de journée"
            >
              <FileText className="w-5 h-5 md:w-6 md:h-6" />
            </Button>
            {/* Bouton Commandes Annulées */}
            <Button
              variant="outline"
              onClick={() => setShowCancelledModal(true)}
              className={`px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border-2 ${cancelledOrders.length > 0 ? 'border-red-500 bg-red-500/10 text-red-400' : 'border-gray-600 text-gray-300'} hover:bg-gray-700 min-h-[44px] flex-shrink-0`}
              title="Commandes annulées"
            >
              <span className="text-lg md:text-xl">🚫</span>
              {cancelledOrders.length > 0 && (
                <span className="ml-1 md:ml-2 bg-red-600 text-white text-xs px-1.5 md:px-2 py-0.5 md:py-1 rounded-full font-bold">
                  {cancelledOrders.length}
                </span>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowSettingsModal(true)}
              className="px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border-2 border-gray-600 text-gray-300 hover:bg-gray-700 min-h-[44px] flex-shrink-0"
            >
              <Settings className="w-5 h-5 md:w-6 md:h-6" />
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowStockModal(true)}
              className={`px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border-2 border-gray-600 ${outOfStockProducts.length > 0 ? 'border-orange-500 text-orange-400' : 'text-gray-300'} hover:bg-gray-700 min-h-[44px] flex-shrink-0`}
            >
              <AlertTriangle className="w-5 h-5 md:w-6 md:h-6" />
              <span className="ml-1 md:ml-2 font-bold">{outOfStockProducts.length}</span>
            </Button>
            <Button
              onClick={togglePause}
              className={`px-3 md:px-5 py-2 md:py-3 text-sm md:text-base min-h-[44px] flex-shrink-0 ${isPaused ? 'bg-red-500 hover:bg-red-600' : 'bg-gray-600 hover:bg-gray-500'}`}
            >
              {isPaused ? <Play className="w-5 h-5 md:w-6 md:h-6" /> : <Pause className="w-5 h-5 md:w-6 md:h-6" />}
            </Button>
            {/* FIX Build 220: Indicateur dernière mise à jour */}
            <div className="hidden md:flex flex-col items-center text-xs text-gray-400 px-2">
              <span>Mise à jour</span>
              <span className="font-medium text-gray-300">{formatTimeAgo(lastUpdate)}</span>
            </div>
            <Button
              variant="outline"
              onClick={handleRefresh}
              disabled={refreshing}
              className="px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border-2 border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50 min-h-[44px] flex-shrink-0"
              title={`Actualiser (${formatTimeAgo(lastUpdate)})`}
            >
              <RefreshCw className={`w-5 h-5 md:w-6 md:h-6 ${refreshing ? 'animate-spin' : ''}`} />
            </Button>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="px-3 py-2 md:px-4 md:py-3 text-sm md:text-base border-2 border-gray-600 text-gray-300 hover:bg-gray-700 min-h-[44px] flex-shrink-0"
            >
              <LogOut className="w-5 h-5 md:w-6 md:h-6" />
            </Button>
          </div>
        </div>
      </div>

      {/* Alerte Pause */}
      {isPaused && (
        <div className="bg-red-500 text-white px-4 py-3 text-center font-bold text-lg animate-pulse">
          ⏸️ COMMANDES EN PAUSE - Les clients ne peuvent pas commander
        </div>
      )}

      {/* Bannière alerte journées non clôturées */}
      {pendingClosures.length > 0 && (
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-7 h-7" />
            <div>
              <span className="font-bold text-lg">
                ⚠️ {pendingClosures.length} journée{pendingClosures.length > 1 ? 's' : ''} non clôturée{pendingClosures.length > 1 ? 's' : ''}
              </span>
              <p className="text-white/90 text-sm mt-1">
                Merci de clôturer : {pendingClosures.map(d => new Date(d.date).toLocaleDateString('fr-FR')).join(', ')}
              </p>
            </div>
          </div>
          <button
            onClick={() => openPendingDayModal(pendingClosures[0])}
            disabled={zLoading}
            className="bg-white text-orange-600 hover:bg-orange-50 font-bold px-6 py-3 rounded-lg text-base flex items-center gap-2"
          >
            {zLoading ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <FileText className="w-5 h-5" />
            )}
            Clôturer les journées →
          </button>
        </div>
      )}

      {/* Bannière alerte clôture veille manquante (fallback si pendingClosures vide) */}
      {pendingClosures.length === 0 && yesterdayStatus && !yesterdayStatus.is_closed && (
        <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AlertTriangle className="w-7 h-7" />
            <div>
              <span className="font-bold text-lg">
                ⚠️ La journée du {new Date(yesterdayStatus.date).toLocaleDateString('fr-FR')} n'est pas clôturée
              </span>
              {yesterdayStatus.pending_orders > 0 && (
                <span className="ml-3 text-white/90">
                  - {yesterdayStatus.pending_orders} commande{yesterdayStatus.pending_orders > 1 ? 's' : ''} en attente
                </span>
              )}
            </div>
          </div>
          <Button
            onClick={() => setShowZModal(true)}
            className="bg-white text-orange-600 hover:bg-orange-50 font-bold px-6 py-3 text-base"
          >
            <Lock className="w-5 h-5 mr-2" />
            Clôturer maintenant
          </Button>
        </div>
      )}

      {/* Bannière mode historique */}
      {selectedDate && (
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-7 h-7" />
            <div>
              <span className="font-bold text-lg">
                📅 Historique du {new Date(selectedDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
              </span>
              <span className="ml-3 text-white/90">
                - {historicOrders.length} commande{historicOrders.length > 1 ? 's' : ''}
                {historicTicketZ && <span className="ml-2">• ✅ Clôturé ({historicTicketZ.total_sales?.toFixed(2)}€)</span>}
              </span>
              {isHistoricDate(selectedDate) && (
                <p className="text-sm text-white/80 mt-1">
                  🔕 Mode régularisation : les notifications ne sont PAS envoyées aux clients
                </p>
              )}
            </div>
          </div>
          <Button
            onClick={backToToday}
            className="bg-white text-blue-600 hover:bg-blue-50 font-bold px-6 py-3 text-base"
          >
            ← Retour aujourd'hui
          </Button>
        </div>
      )}

      {/* Vue Kanban - Commandes du jour OU historique - Responsive */}
      <div className="p-2 md:p-4 h-[calc(100vh-140px)] md:h-[calc(100vh-80px)] overflow-hidden md:overflow-x-auto">
        {/* Mode historique : afficher les commandes d'une date passée */}
        {selectedDate ? (
          <div className="h-full">
            {loadingHistoric ? (
              <div className="flex items-center justify-center h-full">
                <Loader className="w-12 h-12 animate-spin text-blue-500" />
              </div>
            ) : historicOrders.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <Card className="text-center py-8 md:py-16 px-6 md:px-12 bg-gray-800 border-gray-700">
                  <Package className="w-16 md:w-20 h-16 md:h-20 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl md:text-2xl font-bold text-gray-400">Aucune commande ce jour</h3>
                  <p className="text-gray-500 mt-2 text-sm md:text-base">Pas de commandes enregistrées pour cette date</p>
                </Card>
              </div>
            ) : (
              // ============================================
              // HISTORIQUE : RENDU CONDITIONNEL SIMPLE
              // ============================================
              isMobile ? (
                // ===================== VUE MOBILE HISTORIQUE =====================
                <div className="flex flex-col h-full overflow-hidden bg-gray-900">
                  {/* TabBar Mobile pour historique */}
                  <div className="flex-shrink-0 flex overflow-x-auto gap-2 p-3 bg-gray-800 border-b border-gray-700" style={{ WebkitOverflowScrolling: 'touch' }}>
                      {COLUMNS.map((col) => {
                        const count = col.status === 'completed'
                          ? historicOrders.filter(o => ['completed', 'delivered', 'delivering', 'done'].includes(o.status)).length
                          : historicOrders.filter(o => o.status === col.status).length;
                        const isActive = mobileActiveTab === col.id;
                        return (
                          <button
                            key={col.id}
                            onClick={() => setMobileActiveTab(col.id)}
                            className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-bold transition-all min-h-[44px] ${
                              isActive
                                ? `${col.bgColor} text-white shadow-lg`
                                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                            }`}
                          >
                            {col.label.split(' ')[0]} {count > 0 && <span className="ml-1 bg-white/20 px-2 py-0.5 rounded-full">{count}</span>}
                          </button>
                        );
                      })}
                    </div>

                    {/* Liste des commandes historiques pour l'onglet actif */}
                    <div className="flex-1 overflow-y-auto p-3 space-y-3">
                      {(() => {
                        const activeColumn = COLUMNS.find(c => c.id === mobileActiveTab);
                        const columnOrders = activeColumn?.status === 'completed'
                          ? historicOrders.filter(o => ['completed', 'delivered', 'delivering', 'done'].includes(o.status))
                          : historicOrders.filter(o => o.status === activeColumn?.status);

                        if (!columnOrders || columnOrders.length === 0) {
                          return (
                            <div className="text-center py-12 text-gray-400">
                              <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                              <p className="text-sm">Aucune commande</p>
                            </div>
                          );
                        }

                        return columnOrders.map((order) => {
                          const columnConfig = COLUMNS.find(c => c.status === order.status);
                          const getCompletionBadge = (status) => {
                            if (status === 'delivering') return { text: '🚚 En livraison', bg: 'bg-blue-100 text-blue-700' };
                            if (status === 'delivered') return { text: '✅ Livrée', bg: 'bg-green-100 text-green-700' };
                            if (status === 'done') return { text: '✅ Done', bg: 'bg-green-100 text-green-700' };
                            return { text: '🎉 Terminée', bg: 'bg-gray-100 text-gray-700' };
                          };
                          const completionBadge = ['completed', 'delivered', 'delivering', 'done'].includes(order.status)
                            ? getCompletionBadge(order.status)
                            : null;

                          return (
                            <div
                              key={order.id}
                              onClick={() => {
                                setSelectedOrder(order);
                                setShowDetailModal(true);
                              }}
                              className={`bg-white rounded-xl shadow-md border-l-4 p-4 cursor-pointer hover:shadow-lg transition-shadow ${
                                order.status === 'delivered' ? 'border-blue-400' :
                                ['completed', 'done'].includes(order.status) ? 'border-gray-400' :
                                columnConfig?.borderColor?.replace('border-', 'border-l-') || 'border-gray-400'
                              }`}
                            >
                              <div className="flex justify-between items-start mb-2">
                                <div className="flex flex-wrap items-center gap-1">
                                  <span className="font-black text-lg">#{order.order_number || order.id?.slice(0, 6)}</span>
                                  <span className={`px-2 py-0.5 rounded text-xs ${isOrderPaid(order) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                    {isOrderPaid(order) ? '✅ Payé' : '⏳ Non payé'}
                                  </span>
                                  {completionBadge && (
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${completionBadge.bg}`}>
                                      {completionBadge.text}
                                    </span>
                                  )}
                                </div>
                                <span className="text-lg font-bold text-gray-800">{order.total?.toFixed(2)}€</span>
                              </div>
                              <div className="text-sm text-gray-600 mb-3">
                                <p className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  {order.customer_name || 'Client'}
                                </p>
                                {order.items?.slice(0, 2).map((item, i) => (
                                  <p key={i} className={`truncate ${item.from_bogo || item.fromBogo ? 'text-green-600' : 'text-gray-500'}`}>• {item.quantity}x {item.name}{(item.from_bogo || item.fromBogo) && ' 🎁'}</p>
                                ))}
                                {order.items?.length > 2 && (
                                  <p className="text-gray-400 text-xs">+{order.items.length - 2} autres</p>
                                )}
                              </div>

                              {/* Boutons d'action simplifiés */}
                              {order.status !== 'cancelled' && (
                                <div className="flex gap-2 pt-2 border-t">
                                  {!isOrderPaid(order) && (
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedOrder(order);
                                        setShowPaymentModal(true);
                                      }}
                                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-bold"
                                    >
                                      <CreditCard className="w-5 h-5" />
                                      ENCAISSER
                                    </button>
                                  )}
                                  {!['completed', 'delivering', 'delivered', 'done'].includes(order.status) && (
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        if (!isOrderPaid(order)) {
                                          toast.warning('Paiement requis', 'Veuillez encaisser avant de terminer');
                                          setSelectedOrder(order);
                                          setShowPaymentModal(true);
                                          return;
                                        }
                                        setUpdatingOrder(order.id);
                                        try {
                                          setHistoricOrders(prev => prev.map(o =>
                                            o.id === order.id ? { ...o, status: 'completed' } : o
                                          ));
                                          await ordersService.updateStatus(order.id, 'completed');
                                          toast.success('Commande terminée', 'Régularisation effectuée');
                                          checkYesterdayStatus();
                                          checkTodayStatus();
                                        } catch (error) {
                                          console.error('Error:', error);
                                          toast.error('Erreur', 'Impossible de mettre à jour');
                                          loadHistoricData(selectedDate);
                                        } finally {
                                          setUpdatingOrder(null);
                                        }
                                      }}
                                      disabled={updatingOrder === order.id}
                                      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-bold ${
                                        updatingOrder === order.id
                                          ? 'bg-gray-300 text-gray-500'
                                          : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white'
                                      }`}
                                    >
                                      {updatingOrder === order.id ? (
                                        <Loader className="w-5 h-5 animate-spin" />
                                      ) : (
                                        <>
                                          <CheckCircle className="w-5 h-5" />
                                          TERMINÉ
                                        </>
                                      )}
                                    </button>
                                  )}
                                  {order.status === 'completed' && isOrderPaid(order) && (
                                    <div className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-500 rounded-lg font-bold">
                                      <CheckCircle className="w-5 h-5" />
                                      Terminée & Payée
                                    </div>
                                  )}
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedOrder(order);
                                      setShowCancellationModal(true);
                                    }}
                                    className="flex items-center justify-center gap-1 px-3 py-3 bg-red-500 text-white rounded-lg font-bold"
                                  >
                                    <XCircle className="w-5 h-5" />
                                  </button>
                                </div>
                              )}
                            </div>
                          );
                        });
                      })()}
                    </div>
                  </div>
              ) : (
                // ===================== VUE DESKTOP HISTORIQUE =====================
                <div className="flex gap-4 h-full min-w-max pb-0">
                    {COLUMNS.map((column) => {
                      const columnOrders = column.status === 'completed'
                        ? historicOrders.filter(o => ['completed', 'delivered', 'delivering', 'done'].includes(o.status))
                        : historicOrders.filter(o => o.status === column.status);
                      const Icon = column.icon;

                      return (
                        <div
                          key={column.id}
                          className={`flex-1 min-w-[280px] max-w-[380px] flex flex-col rounded-xl ${column.lightBg} border-2 ${column.borderColor} opacity-90`}
                        >
                          {/* Header colonne */}
                          <div className={`${column.headerBg} text-white p-4 rounded-t-lg`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Icon className="w-6 h-6" />
                                <span className="font-bold text-lg">{column.label}</span>
                              </div>
                              <span className="bg-white/20 px-3 py-1 rounded-full font-black text-xl">
                                {columnOrders.length}
                              </span>
                            </div>
                          </div>

                          {/* Liste des commandes historiques */}
                          <div className="flex-1 overflow-y-auto p-3 space-y-3">
                            {columnOrders.length === 0 ? (
                              <div className="text-center py-8 text-gray-400">
                                <p className="text-sm">Aucune commande</p>
                              </div>
                            ) : (
                              columnOrders.map((order) => {
                                const columnConfig = COLUMNS.find(c => c.status === order.status);
                                const getCompletionBadge = (status) => {
                                  if (status === 'delivering') return { text: '🚚 En livraison', bg: 'bg-blue-100 text-blue-700' };
                                  if (status === 'delivered') return { text: '✅ Livrée', bg: 'bg-green-100 text-green-700' };
                                  if (status === 'done') return { text: '✅ Done', bg: 'bg-green-100 text-green-700' };
                                  return { text: '🎉 Terminée', bg: 'bg-gray-100 text-gray-700' };
                                };
                                const completionBadge = ['completed', 'delivered', 'delivering', 'done'].includes(order.status)
                                  ? getCompletionBadge(order.status)
                                  : null;

                                return (
                                  <div
                                    key={order.id}
                                    onClick={() => {
                                      setSelectedOrder(order);
                                      setShowDetailModal(true);
                                    }}
                                    className={`bg-white rounded-xl shadow-md border-l-4 p-4 cursor-pointer hover:shadow-lg transition-shadow ${
                                      order.status === 'delivered' ? 'border-blue-400' :
                                      ['completed', 'done'].includes(order.status) ? 'border-gray-400' :
                                      columnConfig?.borderColor?.replace('border-', 'border-l-') || 'border-gray-400'
                                    }`}
                                  >
                                    <div className="flex justify-between items-start mb-2">
                                      <div className="flex flex-wrap items-center gap-1">
                                        <span className="font-black text-lg">#{order.order_number || order.id?.slice(0, 6)}</span>
                                        <span className={`px-2 py-0.5 rounded text-xs ${isOrderPaid(order) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                          {isOrderPaid(order) ? '✅ Payé' : '⏳ Non payé'}
                                        </span>
                                        {completionBadge && (
                                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${completionBadge.bg}`}>
                                            {completionBadge.text}
                                          </span>
                                        )}
                                      </div>
                                      <span className="text-lg font-bold text-gray-800">{order.total?.toFixed(2)}€</span>
                                    </div>
                                    <div className="text-sm text-gray-600 mb-3">
                                      <p className="flex items-center gap-1">
                                        <User className="w-4 h-4" />
                                        {order.customer_name || 'Client'}
                                      </p>
                                      {order.items?.slice(0, 2).map((item, i) => (
                                        <p key={i} className={`truncate ${item.from_bogo || item.fromBogo ? 'text-green-600' : 'text-gray-500'}`}>• {item.quantity}x {item.name}{(item.from_bogo || item.fromBogo) && ' 🎁'}</p>
                                      ))}
                                      {order.items?.length > 2 && (
                                        <p className="text-gray-400 text-xs">+{order.items.length - 2} autres</p>
                                      )}
                                    </div>

                                    {/* Boutons d'action */}
                                    {order.status !== 'cancelled' && (
                                      <div className="flex gap-2 pt-2 border-t">
                                        {!isOrderPaid(order) && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              setSelectedOrder(order);
                                              setShowPaymentModal(true);
                                            }}
                                            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-lg font-bold hover:from-green-600 hover:to-emerald-700 transition-all shadow-md"
                                          >
                                            <CreditCard className="w-5 h-5" />
                                            ENCAISSER
                                          </button>
                                        )}

                                        {!isOrderPaid(order) && ['card', 'cb', 'stripe'].includes(order.payment_method) && (
                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              try {
                                                toast.info('Vérification...', 'Vérification du paiement sur Stripe');
                                                const res = await paymentsService.verifyStripePayment(order.id);
                                                if (res.updated) {
                                                  toast.success('Paiement vérifié !', 'Le statut a été corrigé');
                                                  loadData();
                                                } else {
                                                  toast.warning('Pas de changement', res.message || `Statut Stripe: ${res.stripe_status}`);
                                                }
                                              } catch (err) {
                                                toast.error('Erreur', getErrorMessage(err));
                                              }
                                            }}
                                            className="flex items-center justify-center gap-1 px-3 py-3 bg-blue-500 text-white rounded-lg font-bold hover:bg-blue-600 transition-all"
                                            title="Vérifier le paiement sur Stripe"
                                          >
                                            💳 Vérifier
                                          </button>
                                        )}

                                        {!['completed', 'delivering', 'delivered', 'done'].includes(order.status) && (
                                          <button
                                            onClick={async (e) => {
                                              e.stopPropagation();
                                              if (!isOrderPaid(order)) {
                                                toast.warning('Paiement requis', 'Veuillez encaisser avant de terminer');
                                                setSelectedOrder(order);
                                                setShowPaymentModal(true);
                                                return;
                                              }
                                              setUpdatingOrder(order.id);
                                              try {
                                                setHistoricOrders(prev => prev.map(o =>
                                                  o.id === order.id ? { ...o, status: 'completed' } : o
                                                ));
                                                await ordersService.updateStatus(order.id, 'completed');
                                                toast.success('Commande terminée', 'Régularisation effectuée (sans notif)');
                                                checkYesterdayStatus();
                                                checkTodayStatus();
                                              } catch (error) {
                                                console.error('Error:', error);
                                                toast.error('Erreur', 'Impossible de mettre à jour');
                                                loadHistoricData(selectedDate);
                                              } finally {
                                                setUpdatingOrder(null);
                                              }
                                            }}
                                            disabled={updatingOrder === order.id}
                                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg font-bold transition-all ${
                                              updatingOrder === order.id
                                                ? 'bg-gray-300 text-gray-500'
                                                : 'bg-gradient-to-r from-blue-500 to-indigo-600 text-white hover:from-blue-600 hover:to-indigo-700 shadow-md'
                                            }`}
                                          >
                                            {updatingOrder === order.id ? (
                                              <Loader className="w-5 h-5 animate-spin" />
                                            ) : (
                                              <>
                                                <CheckCircle className="w-5 h-5" />
                                                TERMINÉ
                                              </>
                                            )}
                                          </button>
                                        )}

                                        {order.status === 'completed' && isOrderPaid(order) && (
                                          <div className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-500 rounded-lg font-bold">
                                            <CheckCircle className="w-5 h-5" />
                                            Terminée & Payée
                                          </div>
                                        )}

                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedOrder(order);
                                            setShowCancellationModal(true);
                                          }}
                                          className="flex items-center justify-center gap-1 px-3 py-3 bg-red-500 text-white rounded-lg font-bold hover:bg-red-600 transition-all"
                                          title="Annuler la commande"
                                        >
                                          <XCircle className="w-5 h-5" />
                                        </button>
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
              )
            )}
          </div>
        ) : (
          /* Mode normal : commandes du jour avec Drag & Drop - Responsive */
          orders.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <Card className="text-center py-8 md:py-16 px-6 md:px-12 bg-gray-800 border-gray-700">
                <Package className="w-16 md:w-20 h-16 md:h-20 text-gray-600 mx-auto mb-4" />
                <h3 className="text-xl md:text-2xl font-bold text-gray-400">Aucune commande active</h3>
                <p className="text-gray-500 mt-2 text-sm md:text-base">Les nouvelles commandes apparaîtront ici</p>
              </Card>
            </div>
          ) : (
            // ============================================
            // RENDU CONDITIONNEL SIMPLE : isMobile ? Mobile : Desktop
            // ============================================
            isMobile ? (
              // ===================== VUE MOBILE =====================
              <div className="flex flex-col h-full overflow-hidden bg-gray-900">
                {/* TabBar Mobile - Barre d'onglets */}
                <div className="flex-shrink-0 flex overflow-x-auto gap-2 p-3 bg-gray-800 border-b border-gray-700" style={{ WebkitOverflowScrolling: 'touch' }}>
                    {COLUMNS.map((col) => {
                      const count = getOrdersForColumn(col.status).length;
                      const isActive = mobileActiveTab === col.id;
                      return (
                        <button
                          key={col.id}
                          onClick={() => setMobileActiveTab(col.id)}
                          className={`flex-shrink-0 px-4 py-2 rounded-full text-sm font-bold transition-all min-h-[44px] ${
                            isActive
                              ? `${col.bgColor} text-white shadow-lg`
                              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                          }`}
                        >
                          {col.label.split(' ')[0]} {count > 0 && <span className="ml-1 bg-white/20 px-2 py-0.5 rounded-full">{count}</span>}
                        </button>
                      );
                    })}
                  </div>

                  {/* Liste des commandes pour l'onglet actif */}
                  <div className="flex-1 overflow-y-auto p-3 space-y-3">
                    {(() => {
                      const activeColumn = COLUMNS.find(c => c.id === mobileActiveTab);
                      const columnOrders = getOrdersForColumn(activeColumn?.status || 'new');

                      if (columnOrders.length === 0) {
                        return (
                          <div className="text-center py-12 text-gray-400">
                            <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                            <p className="text-sm">Aucune commande</p>
                          </div>
                        );
                      }

                      return columnOrders.map((order) => (
                        <OrderCard
                          key={order.id}
                          order={order}
                          column={activeColumn}
                          isUpdating={updatingOrder === order.id}
                          isBlinking={blinkingCancelledIds.has(order.id)}
                          onStatusChange={(newStatus) => requestStatusChange(order, newStatus)}
                          onPayment={() => {
                            setSelectedOrder(order);
                            setShowPaymentModal(true);
                          }}
                          onCancel={() => {
                            setSelectedOrder(order);
                            setShowCancellationModal(true);
                          }}
                          onViewDetails={() => {
                            setSelectedOrder(order);
                            setShowDetailModal(true);
                          }}
                          onPrint={() => printOrder(order)}
                          onKitchenPrint={() => printKitchenOrder(order)}
                          getNextStatus={getNextStatus}
                          formatTime={formatTime}
                          isOrderPaid={isOrderPaid}
                          onVerifyStripe={async (o) => {
                            try {
                              toast.info('Vérification...', 'Vérification du paiement sur Stripe');
                              const res = await paymentsService.verifyStripePayment(o.id);
                              if (res.updated) {
                                toast.success('Paiement vérifié !', 'Le statut a été corrigé');
                                loadData();
                              } else {
                                toast.warning('Pas de changement', res.message || `Statut Stripe: ${res.stripe_status}`);
                              }
                            } catch (err) {
                              toast.error('Erreur', getErrorMessage(err));
                            }
                          }}
                        />
                      ));
                    })()}
                  </div>
                </div>
            ) : (
              // ===================== VUE DESKTOP - KANBAN =====================
              <DndContext
                  sensors={sensors}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  onDragOver={handleDragOver}
                >
                  <div className="flex gap-4 h-full min-w-max pb-0 overflow-y-visible">
                    {COLUMNS.map((column) => {
                      const columnOrders = getOrdersForColumn(column.status);
                      const Icon = column.icon;

                      return (
                        <DroppableColumn key={column.id} id={column.status} column={column}>
                          {/* Header colonne */}
                          <div className={`${column.headerBg} text-white p-4 rounded-t-lg`}>
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                                <Icon className="w-6 h-6" />
                                <span className="font-bold text-lg">{column.label}</span>
                              </div>
                              <span className="bg-white/20 px-3 py-1 rounded-full font-black text-xl">
                                {columnOrders.length}
                              </span>
                            </div>
                          </div>

                          {/* Liste des commandes */}
                          <div className="flex-1 overflow-y-auto p-3 space-y-3">
                            {columnOrders.length === 0 ? (
                              <div className="text-center py-8 text-gray-400">
                                <p className="text-sm">Aucune commande</p>
                                <p className="text-xs mt-1 text-gray-500">Glissez une commande ici</p>
                              </div>
                            ) : (
                              columnOrders.map((order) => (
                                <DraggableOrderCard
                                  key={order.id}
                                  order={order}
                                  column={column}
                                  isUpdating={updatingOrder === order.id}
                                  isDragging={activeId === order.id}
                                  isBlinking={blinkingCancelledIds.has(order.id)}
                                  onStatusChange={(newStatus) => requestStatusChange(order, newStatus)}
                                  onPayment={() => {
                                    setSelectedOrder(order);
                                    setShowPaymentModal(true);
                                  }}
                                  onCancel={() => {
                                    setSelectedOrder(order);
                                    setShowCancellationModal(true);
                                  }}
                                  onViewDetails={() => {
                                    setSelectedOrder(order);
                                    setShowDetailModal(true);
                                  }}
                                  onPrint={() => printOrder(order)}
                                  onKitchenPrint={() => printKitchenOrder(order)}
                                  getNextStatus={getNextStatus}
                                  formatTime={formatTime}
                                  isOrderPaid={isOrderPaid}
                                  onVerifyStripe={async (o) => {
                                    try {
                                      toast.info('Vérification...', 'Vérification du paiement sur Stripe');
                                      const res = await paymentsService.verifyStripePayment(o.id);
                                      if (res.updated) {
                                        toast.success('Paiement vérifié !', 'Le statut a été corrigé');
                                        loadData();
                                      } else {
                                        toast.warning('Pas de changement', res.message || `Statut Stripe: ${res.stripe_status}`);
                                      }
                                    } catch (err) {
                                      toast.error('Erreur', getErrorMessage(err));
                                    }
                                  }}
                                />
                              ))
                            )}
                          </div>
                        </DroppableColumn>
                      );
                    })}
                  </div>

                  {/* Overlay pour l'élément en cours de drag */}
                  <DragOverlay>
                    {draggedOrder ? (
                      <div className="bg-white rounded-xl shadow-2xl border-2 border-blue-500 p-4 opacity-90 transform scale-105">
                        <div className="flex items-center gap-2 mb-2">
                          <GripVertical className="w-5 h-5 text-blue-500" />
                          <span className="font-black text-lg">#{draggedOrder.order_number || draggedOrder.id?.slice(0, 6)}</span>
                        </div>
                        <p className="text-sm text-gray-600">{draggedOrder.customer_name || 'Client'}</p>
                        <p className="text-lg font-bold text-right">{draggedOrder.total?.toFixed(2)}€</p>
                      </div>
                    ) : null}
                  </DragOverlay>
                </DndContext>
            )
          )
        )}
      </div>

      {/* Modal Commandes Annulées */}
      {showCancelledModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white">
            <div className="flex justify-between items-center p-4 border-b bg-red-50">
              <h2 className="text-xl font-bold text-red-700">❌ Commandes Annulées</h2>
              <button onClick={() => setShowCancelledModal(false)}>
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="p-4 overflow-y-auto max-h-[70vh]">
              {cancelledOrders.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <XCircle className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg">Aucune commande annulée</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {cancelledOrders.map((order) => (
                    <div
                      key={order.id}
                      className="p-4 rounded-xl border-2 border-red-200 bg-red-50"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-black text-lg text-gray-800">
                          #{order.order_number || order.id?.slice(0, 6)}
                        </span>
                        <span className="text-sm text-gray-500">
                          {formatDate(order.created_at)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 mb-2">
                        <User className="w-4 h-4 text-gray-400" />
                        <span className="font-semibold text-gray-700">
                          {order.customer_name || 'Client'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-red-600 font-medium">
                          📝 {order.cancel_reason || 'Aucune raison'}
                        </span>
                        <span className="text-xl font-black text-gray-800">
                          {order.total?.toFixed(2)}€
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="p-4 border-t bg-gray-50">
              <Button onClick={() => setShowCancelledModal(false)} className="w-full py-3 text-lg">
                Fermer
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Temps de préparation */}
      {showSettingsModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white">
            <div className="flex justify-between items-center p-5 border-b">
              <h2 className="text-2xl font-bold">⏱️ Temps de préparation</h2>
              <button onClick={() => setShowSettingsModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-8 h-8" />
              </button>
            </div>

            <div className="p-8">
              <p className="text-gray-600 mb-8 text-center text-lg">
                Délai entre la commande et le créneau de récupération
              </p>

              <div className="flex items-center justify-center gap-8">
                <button
                  onClick={() => preparationTime > 5 && updatePreparationTime(preparationTime - 5)}
                  className="w-20 h-20 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors active:scale-95"
                  disabled={preparationTime <= 5}
                >
                  <Minus className="w-10 h-10 text-gray-600" />
                </button>

                <div className="text-center">
                  <span className="text-7xl font-black text-blue-600">{preparationTime}</span>
                  <p className="text-gray-500 text-xl mt-2">minutes</p>
                </div>

                <button
                  onClick={() => updatePreparationTime(preparationTime + 5)}
                  className="w-20 h-20 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center transition-colors active:scale-95"
                >
                  <Plus className="w-10 h-10 text-gray-600" />
                </button>
              </div>

              <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3">
                {[10, 15, 20, 30].map((time) => (
                  <button
                    key={time}
                    onClick={() => updatePreparationTime(time)}
                    className={`py-4 rounded-xl font-bold text-lg transition-colors active:scale-95 ${
                      preparationTime === time
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {time} min
                  </button>
                ))}
              </div>
            </div>

            <div className="p-5 border-t bg-gray-50">
              <Button onClick={() => setShowSettingsModal(false)} className="w-full py-4 text-lg">
                Fermer
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Gestion des Ruptures */}
      {showStockModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] overflow-hidden bg-white">
            <div className="flex justify-between items-center p-5 border-b">
              <h2 className="text-2xl font-bold">🚫 Gestion des Ruptures</h2>
              <button onClick={() => setShowStockModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-8 h-8" />
              </button>
            </div>

            {/* Filtres */}
            <div className="p-5 border-b bg-gray-50">
              <input
                type="text"
                placeholder="🔍 Rechercher un produit..."
                value={searchProduct}
                onChange={(e) => setSearchProduct(e.target.value)}
                className="w-full px-5 py-4 border-2 rounded-xl mb-4 text-lg"
              />
              <div className="flex gap-3">
                <button
                  onClick={() => setStockFilter('all')}
                  className={`px-6 py-3 rounded-full text-base font-bold transition-all active:scale-95 ${stockFilter === 'all' ? 'bg-blue-500 text-white' : 'bg-gray-200'}`}
                >
                  Tous ({products.length})
                </button>
                <button
                  onClick={() => setStockFilter('out_of_stock')}
                  className={`px-6 py-3 rounded-full text-base font-bold transition-all active:scale-95 ${stockFilter === 'out_of_stock' ? 'bg-red-500 text-white' : 'bg-gray-200'}`}
                >
                  En rupture ({outOfStockProducts.length})
                </button>
              </div>
            </div>

            <div className="p-4 overflow-y-auto max-h-[50vh]">
              <div className="space-y-3">
                {filteredProducts.map((product) => {
                  const status = getStockStatus(product);
                  return (
                    <div
                      key={product.id}
                      className={`p-4 rounded-xl border-2 ${
                        status !== 'in_stock'
                          ? 'bg-red-50 border-red-300'
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          {product.image_url && (
                            <img src={product.image_url} alt="" className="w-14 h-14 rounded-lg object-cover" />
                          )}
                          <div>
                            <p className="font-bold text-lg">{product.name}</p>
                            <p className="text-sm text-gray-500">{product.category_name}</p>
                          </div>
                        </div>
                        {status === 'in_stock' && (
                          <span className="px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-700">
                            ✅ En stock
                          </span>
                        )}
                        {status === '24h' && (
                          <span className="px-3 py-1 rounded-full text-sm font-bold bg-orange-100 text-orange-700">
                            ⏰ Rupture 24H
                          </span>
                        )}
                        {status === 'indefinite' && (
                          <span className="px-3 py-1 rounded-full text-sm font-bold bg-red-100 text-red-700">
                            🚫 Indéfinie
                          </span>
                        )}
                      </div>

                      <div className="flex gap-3">
                        {status === 'in_stock' ? (
                          <>
                            <button
                              onClick={() => setProductOutOfStock(product.id, '24h')}
                              className="flex-1 flex items-center justify-center gap-2 px-5 py-4 bg-orange-100 text-orange-700 rounded-xl font-bold text-base hover:bg-orange-200 transition-colors active:scale-95"
                            >
                              <Timer className="w-6 h-6" /> Rupture 24H
                            </button>
                            <button
                              onClick={() => setProductOutOfStock(product.id, 'indefinite')}
                              className="flex-1 flex items-center justify-center gap-2 px-5 py-4 bg-red-100 text-red-700 rounded-xl font-bold text-base hover:bg-red-200 transition-colors active:scale-95"
                            >
                              <Ban className="w-6 h-6" /> Indéfinie
                            </button>
                          </>
                        ) : (
                          <button
                            onClick={() => setProductOutOfStock(product.id, 'in_stock')}
                            className="flex-1 flex items-center justify-center gap-2 px-5 py-4 bg-green-100 text-green-700 rounded-xl font-bold text-base hover:bg-green-200 transition-colors active:scale-95"
                          >
                            <CheckCircle className="w-6 h-6" /> Remettre en stock
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="p-5 border-t bg-gray-50">
              <Button onClick={() => setShowStockModal(false)} className="w-full py-4 text-lg">
                Fermer
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal de Paiement */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => {
          setShowPaymentModal(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
        onSuccess={async () => {
          // SEULEMENT pour les commandes d'un jour passé (Z de la veille) :
          // Après paiement, passer la commande en "completed" automatiquement
          // Pour les commandes du jour, on garde le statut actuel (validation manuelle)
          if (selectedDate && selectedOrder && !['completed', 'delivered', 'done'].includes(selectedOrder.status)) {
            try {
              // FIREBASE DIRECT
              await ordersService.updateStatus(selectedOrder.id, 'completed');
              toast.success('Commande terminée', 'Paiement enregistré et commande terminée (régularisation)');
            } catch (error) {
              console.error('Error updating status:', error);
              // Le paiement a réussi, on ne bloque pas
            }
          } else if (!selectedDate) {
            toast.success('Paiement enregistré', 'Continuez à gérer la commande normalement');
          }

          // Recharger les données selon le contexte (historique ou aujourd'hui)
          if (selectedDate) {
            loadHistoricData(selectedDate);
          } else {
            loadData();
          }
          // Rafraîchir le bandeau "commandes en attente"
          checkYesterdayStatus();
          checkTodayStatus();
          setShowPaymentModal(false);
          setSelectedOrder(null);
        }}
      />

      {/* Modal d'Annulation */}
      <CancellationModal
        isOpen={showCancellationModal}
        onClose={() => {
          setShowCancellationModal(false);
          setSelectedOrder(null);
        }}
        order={selectedOrder}
        onConfirm={handleCancelOrder}
      />

      {/* Modal de Confirmation de statut */}
      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setPendingStatusChange(null);
          setSelectedOrder(null);
        }}
        onConfirm={confirmStatusChange}
        title={pendingStatusChange ? getConfirmationConfig(pendingStatusChange).title : ''}
        message={pendingStatusChange ? getConfirmationConfig(pendingStatusChange).message : ''}
        type={pendingStatusChange ? getConfirmationConfig(pendingStatusChange).type : 'warning'}
        confirmText={pendingStatusChange ? getConfirmationConfig(pendingStatusChange).confirmText : 'Confirmer'}
      />

      {/* Modal Détail Commande */}
      {showDetailModal && selectedOrder && (
        <DetailModal
          order={selectedOrder}
          onClose={() => {
            setShowDetailModal(false);
            setSelectedOrder(null);
          }}
          onStatusChange={(newStatus) => {
            handleQuickStatusChange(selectedOrder, newStatus);
            setShowDetailModal(false);
            setSelectedOrder(null);
          }}
          onPayment={() => {
            setShowDetailModal(false);
            setShowPaymentModal(true);
          }}
          onCancel={() => {
            setShowDetailModal(false);
            setShowCancellationModal(true);
          }}
          onPrint={() => printOrder(selectedOrder)}
          onKitchenPrint={() => printKitchenOrder(selectedOrder)}
          getNextStatus={getNextStatus}
          formatDate={formatDate}
        />
      )}

      {/* Modal Clôture Z */}
      {showZModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-lg bg-white">
            <div className="flex justify-between items-center p-5 border-b">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <FileText className="w-7 h-7 text-green-600" />
                Clôture de Journée (Z)
              </h2>
              <button onClick={() => { setShowZModal(false); setSelectedPendingDay(null); }} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-8 h-8" />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Journée sélectionnée depuis le bandeau pendingClosures */}
              {selectedPendingDay && !selectedPendingDay.is_closed && (
                <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <AlertTriangle className="w-8 h-8 text-orange-500" />
                    <div>
                      <p className="font-bold text-lg text-orange-700">
                        📅 {new Date(selectedPendingDay.date).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
                      </p>
                      <p className="text-orange-600">Non clôturée</p>
                    </div>
                  </div>

                  {/* Résumé */}
                  <div className="bg-white rounded-lg p-4 mb-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">📦 Commandes</span>
                      <span className="font-bold">{selectedPendingDay.total_orders || 0}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">💰 Chiffre d'affaires</span>
                      <span className="font-bold text-green-600">{(selectedPendingDay.total_sales || 0).toFixed(2)}€</span>
                    </div>
                    {selectedPendingDay.pending_orders > 0 && (
                      <div className="flex justify-between items-center text-red-500">
                        <span>⚠️ Non terminées</span>
                        <span className="font-bold">{selectedPendingDay.pending_orders}</span>
                      </div>
                    )}
                  </div>

                  {/* CAS A : Commandes non terminées → Bouton VOIR */}
                  {selectedPendingDay.pending_orders > 0 ? (
                    <>
                      <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-4">
                        <p className="text-orange-800 text-sm">
                          ⚠️ <strong>{selectedPendingDay.pending_orders} commande(s)</strong> doivent être
                          terminées ou annulées avant de pouvoir clôturer.
                        </p>
                      </div>

                      <div className="flex gap-3">
                        <Button
                          onClick={() => {
                            setSelectedPendingDay(null);
                            setShowZModal(false);
                          }}
                          className="flex-1 py-3 bg-gray-200 text-gray-700 hover:bg-gray-300"
                        >
                          Fermer
                        </Button>
                        <Button
                          onClick={() => {
                            setShowZModal(false);
                            setSelectedPendingDay(null);
                            loadHistoricData(selectedPendingDay.date);
                          }}
                          className="flex-1 py-3 bg-blue-500 text-white hover:bg-blue-600"
                        >
                          <Eye className="w-5 h-5 mr-2 inline" />
                          👁️ Voir les commandes
                        </Button>
                      </div>
                    </>
                  ) : (
                    /* CAS B : Toutes terminées → Bouton CLÔTURER */
                    <>
                      <div className="bg-green-100 rounded-lg p-4 mb-4">
                        <p className="text-green-700 font-bold">✅ Toutes les commandes sont terminées ou annulées</p>
                      </div>

                      <div className="flex gap-3">
                        <Button
                          onClick={() => {
                            setSelectedPendingDay(null);
                            setShowZModal(false);
                          }}
                          className="flex-1 py-3 bg-gray-200 text-gray-700 hover:bg-gray-300"
                        >
                          Fermer
                        </Button>
                        <Button
                          onClick={() => handleCloseDay(selectedPendingDay.date, false, '1111')}
                          disabled={zLoading}
                          className="flex-1 py-3 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                        >
                          {zLoading ? (
                            <Loader className="w-5 h-5 animate-spin mx-auto" />
                          ) : (
                            <>
                              <Lock className="w-5 h-5 mr-2 inline" />
                              🔒 Clôturer
                            </>
                          )}
                        </Button>
                      </div>
                    </>
                  )}

                  {/* Bouton pour la journée suivante si plusieurs pending */}
                  {pendingClosures.length > 1 && (
                    <p className="text-center text-sm text-gray-500 mt-3">
                      {pendingClosures.length - 1} autre(s) journée(s) à clôturer
                    </p>
                  )}
                </div>
              )}

              {/* Statut de la veille (seulement si pas de selectedPendingDay) */}
              {!selectedPendingDay && yesterdayStatus && !yesterdayStatus.is_closed && (
                <div className="bg-orange-50 border-2 border-orange-300 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <AlertTriangle className="w-8 h-8 text-orange-500" />
                    <div>
                      <p className="font-bold text-lg text-orange-700">Journée du {new Date(yesterdayStatus.date).toLocaleDateString('fr-FR')}</p>
                      <p className="text-orange-600">Non clôturée</p>
                    </div>
                  </div>

                  {yesterdayStatus.pending_orders > 0 ? (
                    <div className="bg-red-100 rounded-lg p-4 mb-4">
                      <p className="text-red-700 font-bold">
                        ⚠️ {yesterdayStatus.pending_orders} commande{yesterdayStatus.pending_orders > 1 ? 's' : ''} en attente
                      </p>
                      <p className="text-red-600 text-sm mt-1">
                        Toutes les commandes doivent être terminées ou annulées avant la clôture
                      </p>
                      <Button
                        onClick={() => {
                          setShowZModal(false);
                          loadHistoricData(yesterdayStatus.date);
                        }}
                        className="w-full mt-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3"
                      >
                        <Eye className="w-5 h-5 mr-2 inline" />
                        Voir les commandes en attente
                      </Button>
                    </div>
                  ) : (
                    <div className="bg-green-100 rounded-lg p-4 mb-4">
                      <p className="text-green-700 font-bold">✅ Toutes les commandes sont traitées</p>
                      <p className="text-green-600 text-sm mt-1">Vous pouvez clôturer la journée</p>
                    </div>
                  )}

                  <Button
                    onClick={() => handleCloseDay(yesterdayStatus.date)}
                    disabled={!yesterdayStatus.can_close || zLoading}
                    className={`w-full py-4 text-lg font-bold ${
                      yesterdayStatus.can_close
                        ? 'bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700'
                        : 'bg-gray-400'
                    }`}
                  >
                    {zLoading ? (
                      <Loader className="w-6 h-6 animate-spin mx-auto" />
                    ) : (
                      <>
                        <Lock className="w-6 h-6 mr-2 inline" />
                        Clôturer le {new Date(yesterdayStatus.date).toLocaleDateString('fr-FR')}
                      </>
                    )}
                  </Button>
                </div>
              )}

              {/* Hier déjà clôturé - Afficher résumé + bouton imprimer */}
              {yesterdayStatus && yesterdayStatus.is_closed && yesterdayStatus.ticket_z && (
                <div className="bg-green-50 border-2 border-green-300 rounded-xl p-5">
                  <div className="flex items-center gap-3 mb-3">
                    <CheckCircle className="w-8 h-8 text-green-500" />
                    <div>
                      <p className="font-bold text-lg text-green-700">Journée du {new Date(yesterdayStatus.date).toLocaleDateString('fr-FR')}</p>
                      <p className="text-green-600">✅ Clôturée</p>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 mb-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Ventes totales</span>
                      <span className="text-xl font-black text-green-600">{yesterdayStatus.ticket_z.total_sales?.toFixed(2)}€</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Commandes terminées</span>
                      <span className="font-semibold">{yesterdayStatus.ticket_z.completed_orders}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Commandes annulées</span>
                      <span className="font-semibold text-red-600">{yesterdayStatus.ticket_z.cancelled_orders}</span>
                    </div>
                  </div>

                  <Button
                    onClick={() => printTicketZ(yesterdayStatus.ticket_z, yesterdayStatus.date)}
                    className="w-full py-4 text-lg font-bold bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                  >
                    <Printer className="w-6 h-6 mr-2 inline" />
                    Imprimer le Ticket Z d'hier
                  </Button>
                </div>
              )}

              {/* Journée d'aujourd'hui */}
              <div className="bg-blue-50 border-2 border-blue-300 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="font-bold text-lg text-blue-700">Aujourd'hui - {new Date().toLocaleDateString('fr-FR')}</p>
                    <p className="text-blue-600">{orders.length} commande{orders.length > 1 ? 's' : ''} active{orders.length > 1 ? 's' : ''}</p>
                  </div>
                </div>

                {todayStatus?.is_closed ? (
                  <div className="space-y-3">
                    <div className="bg-green-100 rounded-lg p-3 text-green-700 text-sm">
                      ✅ Cette journée a déjà été clôturée
                    </div>
                    {todayStatus.ticket_z && (
                      <>
                        <div className="bg-white rounded-lg p-3 space-y-2">
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600 text-sm">Ventes totales</span>
                            <span className="text-lg font-black text-green-600">{todayStatus.ticket_z.total_sales?.toFixed(2)}€</span>
                          </div>
                          <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Commandes terminées</span>
                            <span className="font-semibold">{todayStatus.ticket_z.completed_orders}</span>
                          </div>
                        </div>
                        <Button
                          onClick={() => printTicketZ(todayStatus.ticket_z, formatDateISO(new Date()))}
                          className="w-full py-3 text-base font-bold bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700"
                        >
                          <Printer className="w-5 h-5 mr-2 inline" />
                          Imprimer le Ticket Z d'aujourd'hui
                        </Button>
                      </>
                    )}
                  </div>
                ) : (
                  <>
                    <p className="text-blue-600 text-sm mb-3">
                      💡 La clôture sera disponible demain après 4h du matin
                    </p>

                    {/* Bouton clôture anticipée */}
                    {!showForceClose ? (
                      <button
                        onClick={() => setShowForceClose(true)}
                        className="text-sm text-gray-500 hover:text-gray-700 underline"
                      >
                        Clôturer maintenant (avancé)
                      </button>
                    ) : (
                      <div className="mt-4 bg-yellow-50 border-2 border-yellow-300 rounded-lg p-4">
                        <p className="text-sm font-semibold text-yellow-800 mb-3">
                          🔐 Clôture anticipée - Code PIN requis
                        </p>

                        {todayStatus?.pending_orders > 0 && (
                          <div className="bg-red-100 rounded-lg p-3 mb-3">
                            <p className="text-red-700 text-sm font-bold">
                              ⚠️ {todayStatus.pending_orders} commande{todayStatus.pending_orders > 1 ? 's' : ''} en attente
                            </p>
                            <button
                              onClick={() => {
                                setShowZModal(false);
                                setShowForceClose(false);
                                backToToday();
                              }}
                              className="mt-2 text-blue-600 text-sm underline hover:text-blue-800"
                            >
                              → Voir les commandes du jour
                            </button>
                          </div>
                        )}

                        <input
                          type="password"
                          inputMode="numeric"
                          maxLength={4}
                          value={forceClosePin}
                          onChange={(e) => setForceClosePin(e.target.value.replace(/\D/g, ''))}
                          placeholder="Code PIN (4 chiffres)"
                          className="w-full px-4 py-3 text-center text-2xl tracking-widest border-2 border-yellow-300 rounded-lg mb-3 focus:border-yellow-500 focus:outline-none"
                        />

                        <div className="flex gap-2">
                          <Button
                            onClick={() => {
                              setShowForceClose(false);
                              setForceClosePin('');
                            }}
                            variant="outline"
                            className="flex-1 py-3"
                          >
                            Annuler
                          </Button>
                          <Button
                            onClick={() => handleCloseDay(formatDateISO(new Date()), true, forceClosePin)}
                            disabled={forceClosePin.length !== 4 || !todayStatus?.can_close || zLoading}
                            className={`flex-1 py-3 ${
                              forceClosePin.length === 4 && todayStatus?.can_close
                                ? 'bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600'
                                : 'bg-gray-400'
                            }`}
                          >
                            {zLoading ? (
                              <Loader className="w-5 h-5 animate-spin mx-auto" />
                            ) : (
                              'Clôturer maintenant'
                            )}
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Info */}
              <div className="bg-gray-100 rounded-lg p-4 text-sm text-gray-600">
                <p className="font-semibold mb-2">Qu'est-ce que le Ticket Z ?</p>
                <p>La clôture de journée génère un récapitulatif comptable : ventes totales, répartition des paiements, TVA collectée, nombre de couverts...</p>
              </div>
            </div>

            <div className="p-5 border-t bg-gray-50">
              <Button onClick={() => setShowZModal(false)} variant="outline" className="w-full py-4 text-lg">
                Fermer
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Sélecteur de date */}
      {showDatePicker && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md bg-white">
            <div className="flex justify-between items-center p-5 border-b">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Calendar className="w-7 h-7 text-blue-600" />
                Charger une journée
              </h2>
              <button onClick={() => setShowDatePicker(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <X className="w-8 h-8" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                Sélectionnez une date passée pour voir les commandes et le ticket Z de ce jour
              </p>

              <input
                type="date"
                max={getYesterdayDate()}
                className="w-full px-5 py-4 border-2 border-gray-300 rounded-xl text-lg focus:border-blue-500 focus:outline-none"
                onChange={(e) => {
                  if (e.target.value) {
                    loadHistoricData(e.target.value);
                  }
                }}
              />

              {loadingHistoric && (
                <div className="mt-6 flex items-center justify-center gap-3 text-blue-600">
                  <Loader className="w-6 h-6 animate-spin" />
                  <span>Chargement...</span>
                </div>
              )}

              {/* Raccourcis */}
              <div className="mt-6">
                <p className="text-sm font-semibold text-gray-500 mb-3">Raccourcis</p>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => loadHistoricData(getYesterdayDate())}
                    className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium text-gray-700 transition-colors"
                  >
                    Hier
                  </button>
                  <button
                    onClick={() => {
                      const d = new Date();
                      d.setDate(d.getDate() - 2);
                      loadHistoricData(formatDateISO(d));
                    }}
                    className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium text-gray-700 transition-colors"
                  >
                    Avant-hier
                  </button>
                  <button
                    onClick={() => {
                      const d = new Date();
                      d.setDate(d.getDate() - 7);
                      loadHistoricData(formatDateISO(d));
                    }}
                    className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium text-gray-700 transition-colors"
                  >
                    Il y a 7 jours
                  </button>
                  <button
                    onClick={() => {
                      const d = new Date();
                      d.setDate(d.getDate() - 30);
                      loadHistoricData(formatDateISO(d));
                    }}
                    className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl font-medium text-gray-700 transition-colors"
                  >
                    Il y a 30 jours
                  </button>
                </div>
              </div>
            </div>

            <div className="p-5 border-t bg-gray-50">
              <Button onClick={() => setShowDatePicker(false)} variant="outline" className="w-full py-4 text-lg">
                Annuler
              </Button>
            </div>
          </Card>
        </div>
      )}

      {/* Modal Résumé Ticket Z historique */}
      {historicTicketZ && selectedDate && (
        <div className="fixed bottom-6 right-6 z-40">
          <Card className="w-80 bg-white shadow-2xl border-2 border-green-300">
            <div className="bg-gradient-to-r from-green-500 to-emerald-600 text-white p-4 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  <span className="font-bold">Ticket Z</span>
                </div>
                <span className="text-sm opacity-90">
                  {new Date(historicTicketZ.closed_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
                </span>
              </div>
            </div>
            <div className="p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Ventes totales</span>
                <span className="text-2xl font-black text-green-600">{historicTicketZ.total_sales?.toFixed(2)}€</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-gray-500">Commandes</p>
                  <p className="font-bold">{historicTicketZ.completed_orders} ✅</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-2">
                  <p className="text-gray-500">Annulées</p>
                  <p className="font-bold text-red-600">{historicTicketZ.cancelled_orders} ❌</p>
                </div>
              </div>
              {historicTicketZ.payment_breakdown && (
                <div className="text-xs space-y-1 pt-2 border-t">
                  <p className="text-gray-500 font-semibold">Répartition paiements</p>
                  {historicTicketZ.payment_breakdown.espece > 0 && (
                    <div className="flex justify-between">
                      <span>💵 Espèces</span>
                      <span className="font-medium">{historicTicketZ.payment_breakdown.espece?.toFixed(2)}€</span>
                    </div>
                  )}
                  {historicTicketZ.payment_breakdown.cb > 0 && (
                    <div className="flex justify-between">
                      <span>💳 CB</span>
                      <span className="font-medium">{historicTicketZ.payment_breakdown.cb?.toFixed(2)}€</span>
                    </div>
                  )}
                  {historicTicketZ.payment_breakdown.online > 0 && (
                    <div className="flex justify-between">
                      <span>🌐 En ligne</span>
                      <span className="font-medium">{historicTicketZ.payment_breakdown.online?.toFixed(2)}€</span>
                    </div>
                  )}
                </div>
              )}
              {/* Bouton Imprimer */}
              <Button
                onClick={() => printTicketZ(historicTicketZ, selectedDate)}
                className="w-full mt-3 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white py-3"
              >
                <Printer className="w-5 h-5 mr-2" />
                Imprimer le Ticket Z
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

// Composant Colonne Droppable pour le Kanban
const DroppableColumn = ({ id, column, children }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: id,
  });

  return (
    <div
      ref={setNodeRef}
      className={`w-full md:flex-1 md:min-w-[320px] md:max-w-[380px] flex flex-col rounded-xl ${column.lightBg} border-2 ${column.borderColor} transition-all duration-200 ${
        isOver ? 'ring-4 ring-blue-400 ring-opacity-50 scale-[1.01]' : ''
      }`}
    >
      {children}
    </div>
  );
};

// FIX Build 201: Composant Carte Draggable - Drag sur zone contenu, PAS sur boutons
const DraggableOrderCard = (props) => {
  const { order, isDragging } = props;

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
  } = useDraggable({
    id: order.id,
  });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
    zIndex: 1000,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`relative ${isDragging ? 'opacity-50 scale-95' : ''}`}
    >
      {/* Indicateur visuel de drag sur le côté gauche */}
      <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-r from-blue-400 to-transparent rounded-l-xl opacity-50" />
      {/* FIX Build 201: Passer les listeners à OrderCard pour drag sur contenu seulement */}
      <OrderCard {...props} dragListeners={listeners} dragAttributes={attributes} />
    </div>
  );
};

// Composant Carte Commande - Vue Kanban avec boutons workflow NORMAL
// FIX Build 201: Ajout dragListeners et dragAttributes pour séparer drag du contenu et boutons
const OrderCard = ({ order, column, isUpdating, onStatusChange, onPayment, onCancel, onViewDetails, onPrint, onKitchenPrint, getNextStatus, formatTime, isOrderPaid, onVerifyStripe, dragListeners, dragAttributes, isBlinking }) => {
  const nextStatus = getNextStatus(order.status, order.order_type);
  const isPaid = isOrderPaid ? isOrderPaid(order) : order.payment_status === 'paid';
  const isCompleted = ['completed', 'delivered', 'delivering', 'done'].includes(order.status);

  // Build 232: Style clignotant pour annulation récente
  const blinkStyle = isBlinking ? 'animate-pulse-cancel border-red-500 shadow-red-200' : '';

  // Badge pour distinguer Terminée vs En livraison vs Livrée
  const getCompletionBadge = () => {
    if (order.status === 'delivering') return { text: '🚚 En livraison', bg: 'bg-blue-100 text-blue-700' };
    if (order.status === 'delivered') return { text: '✅ Livrée', bg: 'bg-green-100 text-green-700' };
    if (order.status === 'done') return { text: '✅ Done', bg: 'bg-green-100 text-green-700' };
    if (order.status === 'completed') return { text: '🎉 Terminée', bg: 'bg-gray-100 text-gray-700' };
    return null;
  };
  const completionBadge = getCompletionBadge();

  // Labels des boutons selon le statut actuel
  const getActionLabel = () => {
    switch (order.status) {
      case 'new': return '🔥 En préparation';
      case 'in_preparation': return '✅ Prête';
      case 'ready': return '🎉 Terminée';
      default: return null;
    }
  };

  // Couleurs des boutons selon le statut (style NORMAL, pas régularisation)
  const getActionStyle = () => {
    switch (order.status) {
      case 'new': return 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600';
      case 'in_preparation': return 'bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-blue-600 hover:to-indigo-600';
      case 'ready': return 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600';
      default: return 'bg-gray-400';
    }
  };

  const actionLabel = getActionLabel();

  return (
    <div
      className={`bg-white rounded-xl shadow-md border-2 transition-all hover:shadow-xl ${
        isUpdating ? 'opacity-50 scale-95' : 'hover:scale-[1.02]'
      } ${!isPaid && !isCompleted ? 'border-orange-400' : 'border-transparent'} ${blinkStyle}`}
    >
      {/* Carte cliquable pour détails - FIX Build 201: Zone draggable ici, pas sur les boutons */}
      <div
        className={`p-3 ${dragListeners ? 'cursor-grab active:cursor-grabbing' : 'cursor-pointer'}`}
        onClick={onViewDetails}
        {...(dragListeners || {})}
        {...(dragAttributes || {})}
      >
        {/* Numéro + Heure + Badge completion */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span className="font-black text-lg text-gray-800">
              #{order.order_number || order.id?.slice(0, 6)}
            </span>
            {completionBadge && (
              <span className={`text-xs font-bold px-2 py-0.5 rounded ${completionBadge.bg}`}>
                {completionBadge.text}
              </span>
            )}
          </div>
          <span className="text-sm text-gray-500 font-medium">
            🕐 {formatTime(order.created_at)}
          </span>
        </div>

        {/* Client */}
        <div className="flex items-center gap-2 mb-2">
          <User className="w-4 h-4 text-gray-400" />
          <span className="font-semibold text-gray-700 truncate">
            {order.customer_name || 'Client'}
          </span>
          {order.customer_phone && (
            <a
              href={`tel:${order.customer_phone}`}
              onClick={(e) => e.stopPropagation()}
              className="text-blue-500 hover:text-blue-700"
            >
              <Phone className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* Créneau de retrait */}
        {order.pickup_time && (
          <div className="text-xs text-indigo-600 font-semibold mb-1 ml-6">
            🕐 Retrait : {order.pickup_time}
          </div>
        )}

        {/* Type + Total */}
        <div className="flex items-center justify-between mb-2">
          <span className={`text-xs font-bold px-2 py-1 rounded-full ${
            order.order_type === 'delivery'
              ? 'bg-blue-100 text-blue-700'
              : order.order_type === 'on_site'
              ? 'bg-green-100 text-green-700'
              : 'bg-purple-100 text-purple-700'
          }`}>
            {order.order_type === 'delivery' ? '🚚 Livraison' : order.order_type === 'on_site' ? '🍽️ Sur place' : '🥡 À emporter'}
          </span>
          <div className="flex items-center gap-2">
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
              order.payment_method === 'card' ? 'bg-blue-50 text-blue-600'
                : (order.payment_method === 'cashback' || order.payment_method === 'loyalty') ? 'bg-amber-50 text-amber-600'
                : 'bg-green-50 text-green-600'
            }`}>
              {order.payment_method === 'card' ? '💳 CB'
                : (order.payment_method === 'cashback' || order.payment_method === 'loyalty') ? '🎁 Fidélité'
                : '💵 Espèces'}
            </span>
            <span className="text-xl font-black text-gray-800">
              {order.total?.toFixed(2)}€
            </span>
          </div>
        </div>

        {/* Paiement en attente */}
        {!isPaid && !isCompleted && (
          <div className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-1 rounded-full text-center mb-2">
            ⚠️ PAIEMENT EN ATTENTE
          </div>
        )}

        {/* Badge terminé avec paiement */}
        {isCompleted && isPaid && (
          <div className="bg-green-100 text-green-700 text-xs font-bold px-2 py-1 rounded-full text-center mb-2">
            ✅ TERMINÉE & PAYÉE
          </div>
        )}

        {/* Articles (résumé) */}
        <div className="text-xs text-gray-500">
          {order.items?.slice(0, 2).map((item, i) => (
            <span key={i}>
              {item.quantity}x {item.name?.substring(0, 15)}{item.name?.length > 15 ? '...' : ''}
              {i < Math.min(order.items.length, 2) - 1 ? ', ' : ''}
            </span>
          ))}
          {order.items?.length > 2 && (
            <span className="text-gray-400"> +{order.items.length - 2} autres</span>
          )}
        </div>

        {/* Notes */}
        {order.notes && (
          <div className="mt-2 text-xs bg-yellow-50 text-yellow-700 px-2 py-1 rounded">
            📝 {order.notes}
          </div>
        )}

        {/* Build 232: Info annulation client */}
        {order.status === 'cancelled' && order.cancelled_by === 'client' && (
          <div className="mt-2 bg-red-100 text-red-700 text-xs font-bold px-2 py-2 rounded">
            <div>❌ Annulée par le client</div>
            {order.cancelled_at && (
              <div className="text-red-500 font-normal mt-1">
                🕐 {new Date(order.cancelled_at?.toDate?.() || order.cancelled_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            {(order.stripe_refund_id || order.loyalty_refunded > 0) && (
              <div className="text-green-600 font-normal mt-1">
                💳 Remboursement effectué
              </div>
            )}
          </div>
        )}
      </div>

      {/* Boutons d'action - Style WORKFLOW NORMAL (pas régularisation) */}
      <div className="border-t p-3 space-y-2">
        {/* Bouton action principale selon le statut */}
        {actionLabel && nextStatus && (
          <button
            onClick={(e) => { e.stopPropagation(); onStatusChange(nextStatus); }}
            disabled={isUpdating}
            className={`w-full py-3 px-4 rounded-xl font-bold text-white text-sm transition-all shadow-md hover:shadow-lg active:scale-95 ${
              isUpdating ? 'bg-gray-400' : getActionStyle()
            }`}
          >
            {isUpdating ? (
              <Loader className="w-5 h-5 animate-spin mx-auto" />
            ) : (
              actionLabel
            )}
          </button>
        )}

        {/* Ligne de boutons secondaires */}
        <div className="flex gap-2">
          {/* Bouton Encaisser si non payé */}
          {!isPaid && (
            <button
              onClick={(e) => { e.stopPropagation(); onPayment(); }}
              className="flex-1 py-2 px-3 rounded-lg font-semibold text-sm bg-emerald-50 text-emerald-700 hover:bg-emerald-100 transition-all flex items-center justify-center gap-1"
            >
              <CreditCard className="w-4 h-4" />
              Encaisser
            </button>
          )}

          {/* Bouton Vérifier Stripe (si CB non payé) */}
          {!isPaid && ['card', 'cb', 'stripe'].includes(order.payment_method) && onVerifyStripe && (
            <button
              onClick={(e) => { e.stopPropagation(); onVerifyStripe(order); }}
              className="py-2 px-3 rounded-lg font-semibold text-xs bg-blue-50 text-blue-700 hover:bg-blue-100 transition-all"
              title="Vérifier le paiement sur Stripe"
            >
              💳
            </button>
          )}

          {/* Bouton Imprimer */}
          <button
            onClick={(e) => { e.stopPropagation(); onPrint(); }}
            className="py-2 px-3 rounded-lg text-blue-600 bg-blue-50 hover:bg-blue-100 transition-all"
            title="Imprimer ticket"
          >
            <Printer className="w-4 h-4" />
          </button>

          {/* Bouton Ticket Cuisine */}
          {onKitchenPrint && (
            <button
              onClick={(e) => { e.stopPropagation(); onKitchenPrint(); }}
              className="py-2 px-3 rounded-lg text-orange-600 bg-orange-50 hover:bg-orange-100 transition-all font-bold text-xs"
              title="Ticket cuisine"
            >
              🍳
            </button>
          )}

          {/* Bouton Annuler */}
          {!isCompleted && order.status !== 'cancelled' && (
            <button
              onClick={(e) => { e.stopPropagation(); onCancel(); }}
              className="py-2 px-3 rounded-lg text-red-600 bg-red-50 hover:bg-red-100 transition-all"
              title="Annuler"
            >
              <XCircle className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

// Modal Détail Commande
const DetailModal = ({ order, onClose, onStatusChange, onPayment, onCancel, onPrint, onKitchenPrint, getNextStatus, formatDate }) => {
  const nextStatus = getNextStatus(order.status, order.order_type);
  const isPaid = order.payment_status === 'paid';
  const isCompleted = order.status === 'completed';

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-black">
                #{order.order_number || order.id?.slice(0, 8)}
              </h2>
              <p className="text-white/80 text-sm">{formatDate(order.created_at)}</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onPrint}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
                title="Imprimer ticket"
              >
                <Printer className="w-5 h-5" />
              </button>
              {onKitchenPrint && (
                <button
                  onClick={onKitchenPrint}
                  className="w-10 h-10 bg-orange-500/40 hover:bg-orange-500/60 rounded-full flex items-center justify-center transition-colors"
                  title="Ticket cuisine"
                >
                  <span className="text-lg">🍳</span>
                </button>
              )}
              <button
                onClick={onClose}
                className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-full flex items-center justify-center transition-colors"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        <div className="p-6 space-y-4">
          {/* Client */}
          <div className="bg-blue-50 p-4 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-black text-xl">
                {(order.customer_name || 'C')[0].toUpperCase()}
              </div>
              <div className="flex-1">
                <p className="font-bold text-lg text-gray-800">{order.customer_name || 'Client'}</p>
                {order.customer_phone && (
                  <a href={`tel:${order.customer_phone}`} className="text-blue-600 text-sm flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {order.customer_phone}
                  </a>
                )}
              </div>
            </div>
          </div>

          {/* Type */}
          <div className={`p-4 rounded-xl ${
            order.order_type === 'delivery' ? 'bg-blue-50' : order.order_type === 'on_site' ? 'bg-green-50' : 'bg-purple-50'
          }`}>
            <div className="flex items-center gap-2">
              {order.order_type === 'delivery' ? (
                <>
                  <Truck className="w-6 h-6 text-blue-600" />
                  <span className="font-bold text-blue-700">Livraison</span>
                </>
              ) : order.order_type === 'on_site' ? (
                <>
                  <span className="text-2xl">🍽️</span>
                  <span className="font-bold text-green-700">Sur place</span>
                </>
              ) : (
                <>
                  <Package className="w-6 h-6 text-purple-600" />
                  <span className="font-bold text-purple-700">À emporter</span>
                </>
              )}
              {(order.pickup_time || order.delivery_time) && (
                <span className="ml-auto text-gray-600 font-medium">
                  🕐 {order.pickup_time || order.delivery_time}
                </span>
              )}
            </div>
          </div>

          {/* Articles */}
          <div>
            <h3 className="font-bold text-gray-700 mb-3">🍽️ Articles</h3>
            <div className="space-y-2">
              {order.items?.map((item, idx) => (
                <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${item.from_bogo || item.fromBogo ? 'bg-green-50 border border-green-200' : 'bg-gray-50'}`}>
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 text-white rounded-full flex items-center justify-center font-bold text-sm ${item.from_bogo || item.fromBogo ? 'bg-green-500' : 'bg-blue-500'}`}>
                      {item.quantity}
                    </span>
                    <div>
                      <p className="font-semibold text-gray-800">
                        {item.name}
                        {(item.from_bogo || item.fromBogo) && <span className="ml-2 text-green-600 text-sm">🎁 OFFERT</span>}
                      </p>
                      {item.options && item.options.length > 0 && (
                        <p className="text-xs text-gray-500">
                          {item.options.map(o => o.name || o).join(', ')}
                        </p>
                      )}
                      {item.notes && (
                        <p className="text-xs text-orange-600">📝 {item.notes}</p>
                      )}
                    </div>
                  </div>
                  <span className={`font-bold ${item.from_bogo || item.fromBogo ? 'text-green-600' : 'text-gray-800'}`}>
                    {item.from_bogo || item.fromBogo ? 'OFFERT' : `${item.total_price?.toFixed(2)}€`}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Notes globales */}
          {order.notes && (
            <div className="bg-yellow-50 border border-yellow-200 p-4 rounded-xl">
              <p className="font-bold text-yellow-800 mb-1">📝 Notes</p>
              <p className="text-yellow-700">{order.notes}</p>
            </div>
          )}

          {/* FIX Build 252: Afficher réductions (promo + fidélité) */}
          {(order.promo_code || order.loyalty_used > 0) && (
            <div className="bg-gray-50 p-4 rounded-xl space-y-2">
              <p className="font-bold text-gray-700 mb-2">💰 Réductions appliquées</p>
              {order.subtotal && (
                <div className="flex justify-between text-gray-600">
                  <span>Sous-total</span>
                  <span>{order.subtotal?.toFixed(2)}€</span>
                </div>
              )}
              {order.promo_code && order.promo_code_discount > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>🏷️ Code promo ({order.promo_code})</span>
                  <span className="font-bold">-{order.promo_code_discount?.toFixed(2)}€</span>
                </div>
              )}
              {order.loyalty_used > 0 && (
                <div className="flex justify-between text-purple-600">
                  <span>💳 Fidélité utilisée</span>
                  <span className="font-bold">-{order.loyalty_used?.toFixed(2)}€</span>
                </div>
              )}
            </div>
          )}

          {/* Total + Paiement */}
          <div className={`p-4 rounded-xl ${isPaid ? 'bg-green-50' : 'bg-orange-50'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {isPaid ? (
                  <>
                    <CheckCircle className="w-6 h-6 text-green-600" />
                    <span className="font-bold text-green-700">Payé</span>
                    <span className="text-sm text-gray-500">({order.payment_method || 'N/A'})</span>
                  </>
                ) : (
                  <>
                    <Clock className="w-6 h-6 text-orange-600" />
                    <span className="font-bold text-orange-700">À payer</span>
                  </>
                )}
              </div>
              <span className="text-3xl font-black text-gray-800">{order.total?.toFixed(2)}€</span>
            </div>
          </div>

          {/* Actions */}
          <div className="space-y-3 pt-4">
            {!isPaid && !isCompleted && (
              <button
                onClick={onPayment}
                className="w-full py-4 bg-gradient-to-r from-green-500 to-emerald-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-2"
              >
                <CreditCard className="w-5 h-5" />
                Enregistrer le paiement
              </button>
            )}

            {nextStatus && (
              <button
                onClick={() => onStatusChange(nextStatus)}
                className="w-full py-4 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transition-all"
              >
                {nextStatus === 'in_preparation' && '🔥 Commencer la préparation'}
                {nextStatus === 'ready' && '✅ Marquer comme prête'}
                {nextStatus === 'delivering' && '🚚 Partir en livraison'}
                {nextStatus === 'completed' && '🎉 Terminer la commande'}
              </button>
            )}

            {!isCompleted && order.status !== 'cancelled' && (
              <button
                onClick={onCancel}
                className="w-full py-3 bg-red-50 text-red-600 rounded-xl font-bold text-lg hover:bg-red-100 transition-all flex items-center justify-center gap-2"
              >
                <XCircle className="w-5 h-5" />
                Annuler la commande
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Export avec Error Boundary
const OrdersModeWithErrorBoundary = () => (
  <OrdersModeErrorBoundary>
    <OrdersMode />
  </OrdersModeErrorBoundary>
);

export default OrdersModeWithErrorBoundary;
